import React, { useState, useRef, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import LEGAL_TEMPLATES from "./legalTemplates";
import { useAuth } from "../contexts/AuthContext";
import { API_BASE } from "../lib/apiBase";
import { FlowLogo, MercadoPagoLogo } from "../components/PaymentBrandLogos";
import { Globe2 } from "lucide-react";
import ConversationsListV2 from "../components/chat/ConversationsListV2";
import ConversationContextMenu from "../components/chat/ConversationContextMenu";
import LabelsModal from "../components/chat/LabelsModal";
import AttachSheet from "../components/chat/AttachSheet";

// Send cookies on every cross-origin request to the API
axios.defaults.withCredentials = true;

const API = API_BASE + "/api";

// Chunked upload settings: 4 MB keeps us well below Cloudflare's request body limits
// on every plan (Free: 100 MB, but some proxies/gateways trim down to ~25 MB).
const CHUNK_UPLOAD_SIZE = 4 * 1024 * 1024;        // 4 MB per chunk
const CHUNK_UPLOAD_THRESHOLD = 4 * 1024 * 1024;   // files > 4 MB go chunked

/**
 * Upload a file to the legal backend using chunked transfer.
 * Calls onProgress({sent, total, chunkIndex, totalChunks}) after each chunk.
 * Returns the same response shape as the single-request /legal/upload
 * (or /legal/expediente/start when kind="expediente").
 */
async function uploadFileChunked(file, { password, sessionId, kind = "normal", onProgress }) {
  const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_UPLOAD_SIZE));

  // 1) init
  const initResp = await fetch(`${API}/legal/upload/chunk/init`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      filename: file.name,
      total_size: file.size,
      total_chunks: totalChunks,
      password,
      session_id: sessionId,
      kind,
    }),
  });
  if (!initResp.ok) {
    let detail = `Error ${initResp.status}`;
    try {
      const j = await initResp.json();
      if (j && j.detail) detail = j.detail;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  const { upload_id: uploadId } = await initResp.json();

  // 2) send each chunk sequentially (simpler, avoids overwhelming the proxy)
  for (let i = 0; i < totalChunks; i++) {
    const start = i * CHUNK_UPLOAD_SIZE;
    const end = Math.min(file.size, start + CHUNK_UPLOAD_SIZE);
    const blob = file.slice(start, end);

    const fd = new FormData();
    fd.append("upload_id", uploadId);
    fd.append("chunk_index", String(i));
    fd.append("password", password);
    fd.append("chunk", blob, `${file.name}.part${i}`);

    let lastErr = null;
    // Retry each chunk up to 2 times on transient network errors / 5xx / 520
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const resp = await fetch(`${API}/legal/upload/chunk/part`, { method: "POST", credentials: "include", body: fd });
        if (resp.ok) {
          lastErr = null;
          break;
        }
        // 4xx other than 429 should not be retried
        if (resp.status >= 400 && resp.status < 500 && resp.status !== 429) {
          let detail = `Error ${resp.status}`;
          try {
            const j = await resp.json();
            if (j && j.detail) detail = j.detail;
          } catch {
            /* ignore */
          }
          throw new Error(detail);
        }
        lastErr = new Error(`Error ${resp.status} al subir fragmento ${i + 1}/${totalChunks}`);
      } catch (err) {
        lastErr = err;
      }
      // Backoff before retry: 500 ms, 1500 ms
      await new Promise((res) => setTimeout(res, 500 + attempt * 1000));
    }
    if (lastErr) throw lastErr;

    if (typeof onProgress === "function") {
      onProgress({
        sent: end,
        total: file.size,
        chunkIndex: i + 1,
        totalChunks,
      });
    }
  }

  // 3) finish → returns the same payload as /legal/upload or /legal/expediente/start
  const finishResp = await fetch(`${API}/legal/upload/chunk/finish`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ upload_id: uploadId, password, kind }),
  });
  if (!finishResp.ok) {
    let detail = `Error ${finishResp.status}`;
    try {
      const j = await finishResp.json();
      if (j && j.detail) detail = j.detail;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  return await finishResp.json();
}

// Generate a per-tab session id (stable while the chat is open)
const _uuid = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : "s-" + Math.random().toString(36).slice(2) + Date.now().toString(36)).replace(/-/g, "").slice(0, 24);

const MODES = [
  { id: "abogado", label: "Abogado", desc: "Respuesta clara y profesional" },
  { id: "profesor", label: "Profesor", desc: "Explicación didáctica" },
  { id: "juez", label: "Juez", desc: "Análisis formal e imparcial" },
];

const INITIAL_GREETING = {
  role: "assistant",
  content:
    "Hola, soy Pre-Legal.cl, tu asistente legal especializado en derecho civil y penal chileno. Cuéntame tu caso o consulta y te ayudaré con gusto.",
  sources: [],
};

// --- localStorage (lightweight boot state only: password, currentConvId, mode) ---
// The heavy data (messages, sessionDocs) lives in MongoDB via /api/legal/conversations.
const STORAGE_KEY = "legal_chat_state_v1";

const loadPersistedState = () => {
  try {
    if (typeof window === "undefined") return {};
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const clearPersistedState = () => {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
};

// Strip heavy/volatile fields from messages before persisting/sending (e.g. streaming flag)
const serializableMessages = (msgs) =>
  (msgs || []).map((m) => {
    const { streaming: _streaming, ...rest } = m;
    return rest;
  });

const persistBootState = (state) => {
  try {
    // Only persist lightweight fields — everything else is in MongoDB
    const light = {
      password: state.password || "",
      currentConvId: state.currentConvId || "",
      mode: state.mode || "abogado",
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(light));
  } catch {
    /* ignore */
  }
};

export default function LegalChatPage() {
  // Read persisted state once on first render (lazy initializers)
  const persisted = (typeof window !== "undefined") ? loadPersistedState() : {};

  // --- Auth (handled by ProtectedRoute + AuthContext) ---
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const password = "";                        // legacy field, backend ignores
  const setPassword = () => {};               // no-op (kept to avoid breaking refs)
  const authed = true;                        // ProtectedRoute already enforced auth
  const setAuthed = () => {};
  const authError = "";
  const setAuthError = () => {};
  const authLoading = false;
  const setAuthLoading = () => {};
  const [showNoConsultasModal, setShowNoConsultasModal] = useState(false);

  // --- Conversation sidebar state ---
  const [conversations, setConversations] = useState([]); // [{conv_id, title, mode, updated_at}]
  const [currentConvId, setCurrentConvId] = useState(persisted.currentConvId || "");
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window === "undefined") return true;
    // Desktop: split view always visible. Mobile: list as entry point.
    return true;
  });
  const [convsLoading, setConvsLoading] = useState(false);
  // Long-press context menu state (V2 redesign)
  const [contextMenuConv, setContextMenuConv] = useState(null);
  const [labelsModalConv, setLabelsModalConv] = useState(null);
  const [attachSheetOpen, setAttachSheetOpen] = useState(false);
  // Bulk selection mode (V2 redesign)
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  // Desktop column resizer
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    if (typeof window === "undefined") return 340;
    const saved = parseInt(localStorage.getItem("prelegal_sidebar_w") || "0", 10);
    return saved >= 240 && saved <= 560 ? saved : 340;
  });
  const resizerActiveRef = useRef(false);

  useEffect(() => {
    const onMove = (e) => {
      if (!resizerActiveRef.current) return;
      const x = e.clientX;
      const next = Math.max(240, Math.min(560, x));
      setSidebarWidth(next);
    };
    const onUp = () => {
      if (resizerActiveRef.current) {
        resizerActiveRef.current = false;
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        try { localStorage.setItem("prelegal_sidebar_w", String(sidebarWidth)); } catch { /* noop */ }
      }
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [sidebarWidth]);

  // --- Chat state (empty until we load/create a conversation) ---
  const [mode, setMode] = useState(persisted.mode || "abogado");
  const [messages, setMessages] = useState([INITIAL_GREETING]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sessionDocs, setSessionDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // {name, sent, total, chunkIndex, totalChunks}
  const [expedienteJob, setExpedienteJob] = useState(null); // {job_id, progress, total, status, filename}
  const [reconnectAttempt, setReconnectAttempt] = useState(0); // 0 = no reconnect in progress; N = Nth invisible retry
  // Brief shine effect across the chat whenever the active conversation changes
  const [shineKey, setShineKey] = useState(0);
  // Templates modal state
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [activeTemplate, setActiveTemplate] = useState(null); // one of LEGAL_TEMPLATES
  const [templateData, setTemplateData] = useState({});       // key -> value as user fills the form
  // sessionId is now bound to the active conversation; falls back to a local uuid if no conv loaded yet
  const sessionIdRef = useRef(persisted.currentConvId || _uuid());

  // Debounced-save machinery: we auto-save the active conversation to the backend
  // a short time after every change, avoiding a hit per-keystroke.
  const saveTimerRef = useRef(null);
  const skipNextSaveRef = useRef(false); // used during conversation load to avoid re-saving just-fetched data
  // Tracks the previous value of `loading` so we can detect the streaming
  // transition (true → false) and trigger a guaranteed save the moment the
  // assistant's reply finishes — independent of any setTimeout/debounce.
  const prevLoadingRef = useRef(false);

  // Saving status indicator shown in header: 'idle' | 'saving' | 'saved' | 'error'
  const [savingStatus, setSavingStatus] = useState("idle");
  const savedToastTimerRef = useRef(null);

  // Refs that always hold the LATEST state — used by keepalive save on
  // window close (where event handlers are attached once and would otherwise
  // capture stale closures). This is what lets us guarantee a save survives
  // tab/browser close.
  const messagesRef = useRef([]);
  const sessionDocsRef = useRef([]);
  const modeRef = useRef("abogado");
  const currentConvIdRef = useRef("");
  // Function ref that always points to the latest saveCurrentConversation
  // closure, so any callback (handleLogout, after-streaming, after-upload)
  // can call saveRef.current() and get the latest state.
  const saveRef = useRef(null);

  const scrollRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  // Detect mobile to adapt placeholders & UI text
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 768 : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Auto-grow textarea based on content
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    const max = 260;
    ta.style.height = Math.min(ta.scrollHeight, max) + "px";
  }, [input]);

  // Quote a message into the input (used by swipe-to-reply gesture)
  const handleQuote = useCallback((rawText) => {
    if (!rawText || typeof rawText !== "string") return;
    // Strip document markers, collapse to a tidy snippet
    const clean = rawText
      .replace(/--- INICIO DOCUMENTO ---|--- FIN DOCUMENTO ---/g, "")
      .trim();
    const snippet = clean.length > 200 ? clean.slice(0, 200).trim() + "…" : clean;
    const quoted = snippet
      .split("\n")
      .map((l) => `> ${l}`)
      .join("\n");
    setInput((prev) => (prev ? `${quoted}\n\n${prev}` : `${quoted}\n\n`));
    // Focus textarea after the state update
    setTimeout(() => {
      const ta = textareaRef.current;
      if (ta) {
        ta.focus();
        // Place caret at the very end
        const len = ta.value.length;
        try { ta.setSelectionRange(len, len); } catch { /* noop */ }
      }
    }, 60);
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // ----------------------------------------------------------------------
  // Conversation management helpers
  // ----------------------------------------------------------------------
  const fetchConversations = async () => {
    try {
      setConvsLoading(true);
      const resp = await axios.get(`${API}/legal/conversations`, {
        // Cache-bust: guarantees we always get fresh data from MongoDB,
        // never a stale response from Cloudflare/browser cache.
        params: { _ts: Date.now() },
      });
      setConversations(resp.data.conversations || []);
      return resp.data.conversations || [];
    } catch (err) {
      if (err.response && err.response.status === 401) {
        clearPersistedState();
      }
      return [];
    } finally {
      setConvsLoading(false);
    }
  };

  const createConversation = async (initialMessages, initialDocs, initialMode) => {
    try {
      const resp = await axios.post(`${API}/legal/conversations`, {});
      const conv = resp.data;
      // Optional: seed with initial messages/docs/mode (used for migration of old localStorage)
      if ((initialMessages && initialMessages.length) || (initialDocs && initialDocs.length)) {
        await axios.put(`${API}/legal/conversations/${conv.conv_id}`, {
          messages: serializableMessages(initialMessages || []),
          session_docs: initialDocs || [],
          mode: initialMode || "abogado",
        });
      }
      return conv;
    } catch (err) {
      setError("No se pudo crear la conversación: " + (err.message || err));
      throw err;
    }
  };

  const loadConversationById = async (convId) => {
    if (!convId) return false;
    try {
      const resp = await axios.get(`${API}/legal/conversations/${convId}`, {
        // Cache-bust to avoid stale messages from CDN/proxy cache.
        params: { _ts: Date.now() },
      });
      const c = resp.data;
      // Avoid triggering the auto-save right after we just loaded from the server
      skipNextSaveRef.current = true;
      setCurrentConvId(c.conv_id);
      sessionIdRef.current = c.session_id || c.conv_id;
      setMode(c.mode || "abogado");
      setMessages(
        Array.isArray(c.messages) && c.messages.length > 0 ? c.messages : [INITIAL_GREETING]
      );
      setSessionDocs(Array.isArray(c.session_docs) ? c.session_docs : []);
      setError("");
    } catch (err) {
      if (err.response && err.response.status === 404) {
        setError("La conversación guardada ya no existe; se creará una nueva.");
        setCurrentConvId("");
      } else {
        setError("No se pudo cargar la conversación.");
      }
    }
  };

  const saveCurrentConversation = async () => {
    if (!currentConvId) return;
    setSavingStatus("saving");
    try {
      await axios.put(`${API}/legal/conversations/${currentConvId}`, {
        messages: serializableMessages(messages),
        session_docs: sessionDocs,
        mode,
      });
      setSavingStatus("saved");
      // Clear the "saved" badge after 2s so it doesn't linger forever
      if (savedToastTimerRef.current) clearTimeout(savedToastTimerRef.current);
      savedToastTimerRef.current = setTimeout(() => setSavingStatus("idle"), 2000);
      // Refresh sidebar metadata (title may have changed on first user msg)
      fetchConversations();
    } catch (err) {
      if (err.response && err.response.status === 401) {
        clearPersistedState();
      }
      setSavingStatus("error");
      // Silent failure otherwise — we'll try again on the next change
    }
  };

  // Keep saveRef pointing to the LATEST saveCurrentConversation closure so
  // window-event handlers (which are attached once) can always reach the
  // current state via saveRef.current().
  saveRef.current = saveCurrentConversation;

  // Sync state into refs so the keepalive (page-close) save can read the
  // latest snapshot from any context.
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);
  useEffect(() => {
    sessionDocsRef.current = sessionDocs;
  }, [sessionDocs]);
  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);
  useEffect(() => {
    currentConvIdRef.current = currentConvId;
  }, [currentConvId]);

  // Synchronous save via fetch keepalive — survives tab/window close.
  // The browser keeps this request alive even after the page unloads.
  // We also try navigator.sendBeacon as a fallback because iOS Safari and
  // some Android browsers throttle fetch keepalive when the tab is closing
  // but reliably honor sendBeacon (it's specifically designed for this).
  const saveSnapshotKeepalive = useCallback(() => {
    const id = currentConvIdRef.current;
    if (!id) return;
    const url = `${API}/legal/conversations/${id}`;
    let body;
    try {
      body = JSON.stringify({
        messages: serializableMessages(messagesRef.current),
        session_docs: sessionDocsRef.current,
        mode: modeRef.current,
      });
    } catch {
      return;
    }
    // Strategy 1: navigator.sendBeacon — most reliable on close (especially
    // iOS Safari). It uses POST + Blob, so we send to a beacon-friendly
    // endpoint via the same PUT URL using a Blob with JSON content-type.
    let beaconOk = false;
    try {
      if (typeof navigator !== "undefined" && navigator.sendBeacon) {
        const blob = new Blob([body], { type: "application/json" });
        // Beacon issues POST, not PUT, so we hit a backend endpoint that
        // accepts POST as an alias. If your backend has only PUT, this
        // will be a no-op for beacon — fetch keepalive below covers it.
        beaconOk = navigator.sendBeacon(`${url}/beacon`, blob);
      }
    } catch {
      /* ignore */
    }
    // Strategy 2: fetch with keepalive: true (modern Chrome/Edge/Firefox)
    try {
      fetch(url, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      });
    } catch {
      /* ignore — page is closing */
    }
    return beaconOk;
  }, []);

  // Window/tab close + visibility change: flush the latest snapshot so
  // the user never loses messages just because they closed the browser.
  useEffect(() => {
    if (!authed) return;
    const onPageHide = () => saveSnapshotKeepalive();
    const onBeforeUnload = () => saveSnapshotKeepalive();
    const onVisibilityChange = () => {
      // When the tab becomes hidden (user switches tabs, locks phone,
      // backgrounds Safari) — flush as a precaution. iOS Safari often
      // skips beforeunload but does fire visibilitychange.
      if (document.visibilityState === "hidden") saveSnapshotKeepalive();
    };
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("beforeunload", onBeforeUnload);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("beforeunload", onBeforeUnload);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [authed, saveSnapshotKeepalive]);

  const deleteConversationById = async (convId) => {
    if (!convId) return;
    if (!window.confirm("¿Eliminar esta conversación? Esta acción no se puede deshacer.")) return;
    try {
      await axios.delete(`${API}/legal/conversations/${convId}`);
      const remaining = conversations.filter((c) => c.conv_id !== convId);
      setConversations(remaining);
      if (currentConvId === convId) {
        // Load the next most recent, or create a fresh one
        if (remaining.length > 0) {
          await loadConversationById(remaining[0].conv_id);
        } else {
          const c = await createConversation();
          setConversations([
            { conv_id: c.conv_id, title: c.title, mode: c.mode, updated_at: c.updated_at },
          ]);
          skipNextSaveRef.current = true;
          setCurrentConvId(c.conv_id);
          sessionIdRef.current = c.session_id;
          setMessages([INITIAL_GREETING]);
          setSessionDocs([]);
        }
      }
      // Re-fetch from server so the sidebar matches the canonical state in MongoDB
      // (handles the case where another device has its own pending changes).
      fetchConversations();
    } catch (err) {
      setError("No se pudo eliminar la conversación.");
    }
  };

  const renameConversation = async (convId, newTitle) => {
    if (!convId || !newTitle) return;
    try {
      await axios.put(`${API}/legal/conversations/${convId}`, {
        title: newTitle.trim(),
      });
      setConversations((prev) =>
        prev.map((c) => (c.conv_id === convId ? { ...c, title: newTitle.trim() } : c))
      );
    } catch (err) {
      setError("No se pudo renombrar la conversación.");
    }
  };

  // PATCH conversation metadata (favorite, status, tag). Used by long-press menu.
  const patchConversation = async (convId, patch) => {
    if (!convId || !patch) return;
    try {
      const resp = await axios.patch(`${API}/legal/conversations/${convId}`, patch);
      const updated = resp?.data || {};
      setConversations((prev) =>
        prev.map((c) =>
          c.conv_id === convId
            ? {
                ...c,
                favorite: updated.favorite ?? c.favorite,
                status: updated.status ?? c.status,
                tag: "tag" in updated ? updated.tag : c.tag,
                updated_at: updated.updated_at || c.updated_at,
              }
            : c
        )
      );
    } catch (err) {
      setError("No se pudo actualizar la consulta.");
    }
  };

  const regenerateConvTitle = async (convId) => {
    try {
      const resp = await axios.post(`${API}/legal/conversations/${convId}/regenerate-title`, {});
      setConversations((prev) =>
        prev.map((c) => (c.conv_id === convId ? { ...c, title: resp.data.title } : c))
      );
    } catch {
      /* silent — user can still rename manually */
    }
  };

  // Download a single conversation as a printable PDF transcript. Triggers a
  // browser download (blob -> anchor click) so the user gets `prelegal-<title>.pdf`.
  const downloadConvPdf = async (convId, title) => {
    if (!convId) return;
    // If we're downloading the active conversation and there's a pending save,
    // flush it first so the PDF reflects the latest state.
    if (convId === currentConvId) {
      try {
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        if (saveRef.current) await saveRef.current();
      } catch {
        /* ignore — still try the download */
      }
    }
    try {
      const resp = await axios.get(
        `${API}/legal/conversations/${convId}/export-pdf`,
        { responseType: "blob" }
      );
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safeTitle = (title || "conversacion")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .slice(0, 40);
      a.download = `prelegal-${safeTitle}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Free up the object URL after a moment
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (err) {
      const detail =
        err.response?.data?.detail ||
        err.message ||
        "No se pudo descargar el PDF.";
      setError(detail);
    }
  };

  // ----------------------------------------------------------------------
  // Persist the lightweight boot state (password, currentConvId, mode) to localStorage
  // so the next page load can skip the login prompt and auto-select the last conversation.
  // ----------------------------------------------------------------------
  useEffect(() => {
    if (!authed) return;
    persistBootState({ password, currentConvId, mode });
  }, [authed, password, currentConvId, mode]);

  // Trigger a subtle "shine" whenever the active conversation changes (while logged in).
  useEffect(() => {
    if (!authed || !currentConvId) return;
    setShineKey((k) => k + 1);
  }, [currentConvId, authed]);

  // ----------------------------------------------------------------------
  // On mount (and whenever authed flips true): always fetch the conversation
  // list from MongoDB (never from localStorage) so all devices show the same
  // canonical state. Validates any persisted currentConvId against the live
  // server list, drops it if stale, and falls back to the most recent conv
  // or creates one if the user has none.
  // ----------------------------------------------------------------------
  useEffect(() => {
    if (!authed) return;
    let cancelled = false;
    (async () => {
      // 1. Fetch conversation list (always from server, never cached)
      const list = await fetchConversations();
      if (cancelled) return;

      // 2. Decide which conversation to load
      let targetId = currentConvId;
      if (targetId && !list.find((c) => c.conv_id === targetId)) {
        // The persisted currentConvId was deleted on another device — drop it
        targetId = "";
        setCurrentConvId("");
      }

      // 3. Legacy migration: if the old localStorage had inline messages, create a
      //    new conversation with them and promote it to the active one.
      try {
        const rawLegacy = window.localStorage.getItem(STORAGE_KEY);
        const legacy = rawLegacy ? JSON.parse(rawLegacy) : null;
        const legacyMsgs = legacy && Array.isArray(legacy.messages) ? legacy.messages : null;
        const legacyDocs = legacy && Array.isArray(legacy.sessionDocs) ? legacy.sessionDocs : null;
        const hasInterestingLegacy =
          (legacyMsgs && legacyMsgs.length > 1) || (legacyDocs && legacyDocs.length > 0);
        if (!targetId && hasInterestingLegacy) {
          const conv = await createConversation(
            legacyMsgs || [],
            legacyDocs || [],
            (legacy && legacy.mode) || mode
          );
          targetId = conv.conv_id;
          setCurrentConvId(targetId);
          // Refresh the sidebar
          fetchConversations();
        }
      } catch {
        /* ignore legacy parse errors */
      }
      if (cancelled) return;

      // 4. Load the target conversation, or create a fresh one if the list is empty
      if (targetId) {
        await loadConversationById(targetId);
      } else if (list.length > 0) {
        await loadConversationById(list[0].conv_id);
      } else {
        try {
          const conv = await createConversation([], [], mode);
          if (cancelled) return;
          setConversations([
            { conv_id: conv.conv_id, title: conv.title, mode: conv.mode, updated_at: conv.updated_at },
          ]);
          skipNextSaveRef.current = true;
          setCurrentConvId(conv.conv_id);
          sessionIdRef.current = conv.session_id;
          setMessages([INITIAL_GREETING]);
          setSessionDocs([]);
        } catch (_err) {
          // Auth interceptor already redirects to /login on 401 refresh failure.
          // Any other error was already surfaced via setError inside createConversation.
        }
      }
    })().catch(() => {
      /* swallow any uncaught rejection from the bootstrap IIFE to avoid
         surfacing "Uncaught runtime errors" overlays in dev on transient 401s */
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  // ----------------------------------------------------------------------
  // Cross-device sync: refetch the conversation list whenever the tab becomes
  // visible or regains focus. This way, if the user deletes/renames a conv on
  // another device (or in another tab), the sidebar updates automatically next
  // time they look at this tab — without needing a manual reload.
  // ----------------------------------------------------------------------
  useEffect(() => {
    if (!authed) return;
    const refetchIfActive = () => {
      if (document.visibilityState === "visible") {
        fetchConversations().then((list) => {
          // If the currently-active conversation was deleted elsewhere, fall
          // back to the most recent one (or create a fresh empty conversation).
          if (currentConvId && !list.find((c) => c.conv_id === currentConvId)) {
            setCurrentConvId("");
            if (list.length > 0) {
              loadConversationById(list[0].conv_id);
            }
          }
        });
      }
    };
    window.addEventListener("focus", refetchIfActive);
    document.addEventListener("visibilitychange", refetchIfActive);
    return () => {
      window.removeEventListener("focus", refetchIfActive);
      document.removeEventListener("visibilitychange", refetchIfActive);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, currentConvId]);

  // ----------------------------------------------------------------------
  // Auto-save: debounce writes to MongoDB every 1.5s after the user stops typing/receiving.
  // ----------------------------------------------------------------------
  useEffect(() => {
    if (!authed || !currentConvId) return;
    if (skipNextSaveRef.current) {
      skipNextSaveRef.current = false;
      return;
    }
    // Don't save while an assistant reply is still streaming — the partial text would be
    // overwritten by the final version moments later. We'll save once streaming completes.
    if (loading) return;

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveCurrentConversation();
    }, 800);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, sessionDocs, mode, currentConvId, authed, loading]);

  // ----------------------------------------------------------------------
  // GUARANTEED save on streaming completion (defense in depth).
  //
  // The auto-save useEffect above skips while `loading` is true to avoid
  // overwriting the in-progress reply. That means during the 20-40 seconds
  // of streaming, no debounce gets scheduled. If the user closes the tab
  // a fraction of a second after the reply finishes, the post-streaming
  // setTimeout(50ms) might not have flushed yet either.
  //
  // This effect watches the `loading` flag transitioning from true → false
  // (i.e. streaming just ended) and triggers an IMMEDIATE save with no
  // debounce. It complements the inline setTimeout in handleSend() and
  // ensures persistence even if that timer is interrupted by a tab close.
  // ----------------------------------------------------------------------
  useEffect(() => {
    if (prevLoadingRef.current && !loading) {
      // Cancel any pending debounce so it doesn't double-save
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
      if (authed && currentConvId && saveRef.current) {
        saveRef.current();
      }
    }
    prevLoadingRef.current = loading;
  }, [loading, authed, currentConvId]);

  const handleAuth = async (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    setAuthLoading(true);
    setAuthError("");
    try {
      await axios.post(`${API}/legal/auth`, { password });
      setAuthed(true);
    } catch (err) {
      setAuthError(err.response?.data?.detail || "Contraseña inválida");
    } finally {
      setAuthLoading(false);
    }
  };

  const handleSend = async (e, overrideText) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    const text = (typeof overrideText === "string" ? overrideText : input).trim();
    if ((!text && sessionDocs.length === 0) || loading) return;

    // Build display content for user bubble. We mark which docs are newly introduced
    // this turn vs those persisting from prior turns, so the bot can acknowledge newness.
    const displayContent = text || (sessionDocs.length > 0 ? "(usa los documentos adjuntos)" : "");
    const attachmentIds = sessionDocs.map((d) => d.doc_id).filter(Boolean);
    const docsForDisplay = sessionDocs.map(({ kind, name }) => ({ kind, name }));

    const newUserMsg = { role: "user", content: displayContent, attachments: docsForDisplay, t: new Date().toISOString() };
    const nextMessages = [...messages, newUserMsg];
    // Add a placeholder assistant message that we'll fill as chunks arrive
    setMessages([...nextMessages, { role: "assistant", content: "", sources: [], streaming: true, t: new Date().toISOString() }]);
    setInput("");
    // NOTE: do NOT clear sessionDocs — they persist across turns until user removes them.
    setError("");
    setLoading(true);
    setReconnectAttempt(0);

    // Build the base history that the API expects (role+content only).
    const baseHistory = nextMessages
      .filter((m) => m.role === "user" || m.role === "assistant")
      .map(({ role, content }) => ({ role, content: typeof content === "string" ? content : "" }));
    if (baseHistory.length > 0 && baseHistory[baseHistory.length - 1].role === "user") {
      baseHistory[baseHistory.length - 1].content = text || "Por favor revisa los documentos que te adjunté.";
    }

    // Accumulators that persist across retries
    const acc = { text: "", sources: [] };
    const MAX_RETRIES = 3;
    let attempt = 0;
    let terminalError = null;

    // Helper: build the messages payload. If we already have partial text, we append
    // the partial assistant turn + a system-ish user turn asking to continue.
    const buildMessagesForAttempt = () => {
      if (!acc.text) return baseHistory;
      return [
        ...baseHistory,
        { role: "assistant", content: acc.text },
        {
          role: "user",
          content:
            "[SISTEMA] La conexión se cortó. Continúa EXACTAMENTE desde donde te quedaste, " +
            "SIN repetir nada de lo ya escrito y SIN preámbulos ni disculpas. " +
            "Reanuda la redacción directamente.",
        },
      ];
    };

    try {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (attempt > 0) {
          setReconnectAttempt(attempt);
          // Backoff before retry: 1s, 2s, 4s
          await new Promise((r) => setTimeout(r, Math.min(4000, 1000 * Math.pow(2, attempt - 1))));
        }

        const result = await doStreamRequest(
          {
            messages: buildMessagesForAttempt(),
            mode,
            password,
            attachment_ids: attachmentIds,
            session_id: sessionIdRef.current,
          },
          // onSources — only accept on the first attempt (subsequent retries are continuations)
          (srcs) => {
            if (attempt === 0 && srcs) {
              acc.sources = srcs;
              setMessages((prev) => {
                const copy = [...prev];
                copy[copy.length - 1] = { ...copy[copy.length - 1], sources: acc.sources };
                return copy;
              });
            }
          },
          // onDelta
          (delta) => {
            acc.text += delta;
            setMessages((prev) => {
              const copy = [...prev];
              copy[copy.length - 1] = { ...copy[copy.length - 1], content: acc.text };
              return copy;
            });
          }
        );

        if (result.status === "done") {
          break; // success
        }
        if (result.status === "auth_fail") {
          clearPersistedState();
          setAuthed(false);
          setPassword("");
          terminalError = "Sesión expirada, vuelve a ingresar.";
          break;
        }
        if (result.status === "no_consultas") {
          // Pop the placeholder assistant bubble — we never got any text
          setMessages((prev) => {
            const last = prev[prev.length - 1];
            if (last && last.role === "assistant" && !last.content) return prev.slice(0, -1);
            return prev;
          });
          // Refresh user balance + show modal
          await refreshUser();
          setShowNoConsultasModal(true);
          terminalError = "";  // we handle it via modal, no inline error needed
          break;
        }
        if (result.status === "client_error") {
          terminalError = result.error || "Error en la solicitud";
          break;
        }
        // ── transient_error ──
        // The stream broke mid-flight (WebSocket dropped, network blip, etc.).
        // CRITICAL: persist the partial assistant text to MongoDB NOW, BEFORE
        // we start the backoff + retry. If the user refreshes during the
        // "Reconectando..." indicator (which is exactly what bug report #X
        // described), the save-on-completion guarantee never fires because
        // `setLoading(false)` is still in the `finally` block far away.
        //
        // This explicit save uses `fetch keepalive: true` so it survives a
        // tab close, AND we await its in-flight request so the chained retry
        // doesn't race with it.
        try {
          if (currentConvIdRef.current) {
            // Mark the in-flight assistant bubble as "no longer streaming" in
            // the SNAPSHOT we send (don't mutate React state — that would
            // hide the live cursor in the UI).
            const snapshot = (messagesRef.current || []).map((m, idx, arr) => {
              if (idx === arr.length - 1 && m.role === "assistant") {
                return { ...m, content: acc.text, streaming: false };
              }
              return m;
            });
            await axios.put(
              `${API}/legal/conversations/${currentConvIdRef.current}`,
              {
                messages: serializableMessages(snapshot),
                session_docs: sessionDocsRef.current,
                mode: modeRef.current,
              }
            );
          }
        } catch (saveErr) {
          // Don't block the retry — but log so we can see it in DevTools
          // eslint-disable-next-line no-console
          console.warn("[LEGAL] mid-stream save failed (will retry on completion):", saveErr);
        }
        attempt++;
        if (attempt > MAX_RETRIES) {
          terminalError = acc.text
            ? "La conexión falló varias veces. Lo que alcancé a redactar quedó arriba — puedes pedirme que continúe."
            : result.error || "No se pudo conectar después de varios intentos.";
          break;
        }
        // else: loop again (the next iteration will show reconnect indicator + backoff)
      }

      if (terminalError) {
        throw new Error(terminalError);
      }

      // Mark last message as done streaming
      setMessages((prev) => {
        const copy = [...prev];
        const last = copy[copy.length - 1];
        if (last && last.role === "assistant") {
          copy[copy.length - 1] = { ...last, streaming: false };
        }
        return copy;
      });
      // Refresh user balance (consultas_remaining) after a successful exchange
      refreshUser();
      // CRITICAL: flush save IMMEDIATELY after streaming completes so the
      // user never loses the response if they close the window before the
      // 800ms debounce fires. The state update above (setMessages) hasn't
      // been applied to the closure yet, so we wait one tick.
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      setTimeout(() => {
        if (saveRef.current) saveRef.current();
      }, 50);
    } catch (err) {
      const msg = err.message || "Error desconocido";
      setError(msg);
      // Keep partial content if we have it (user can still read what was generated)
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last && last.role === "assistant" && !last.content) {
          return prev.slice(0, -1);
        }
        // If we have partial text, just mark it as no-longer-streaming
        if (last && last.role === "assistant" && last.streaming) {
          const copy = [...prev];
          copy[copy.length - 1] = { ...last, streaming: false };
          return copy;
        }
        return prev;
      });
      // CRITICAL save: even on terminal errors (max retries exceeded, network
      // dead, etc.), persist whatever partial text we managed to accumulate.
      // The save-on-loading-edge useEffect WILL also fire, but redundancy is
      // cheap and we want zero data loss for the Safari/WebSocket bug.
      try {
        if (currentConvIdRef.current) {
          const snapshot = (messagesRef.current || []).map((m, idx, arr) => {
            if (idx === arr.length - 1 && m.role === "assistant") {
              return { ...m, content: acc.text, streaming: false };
            }
            return m;
          });
          await axios.put(
            `${API}/legal/conversations/${currentConvIdRef.current}`,
            {
              messages: serializableMessages(snapshot),
              session_docs: sessionDocsRef.current,
              mode: modeRef.current,
            }
          );
        }
      } catch {
        /* swallow — keepalive on close will also try */
      }
    } finally {
      setLoading(false);
      setReconnectAttempt(0);
    }
  };

  // Run a single SSE streaming request. Returns a detailed result
  // describing how the stream ended (so handleSend can decide whether to retry).
  //   status: "done"           → stream ended with explicit "done" event (success)
  //           "auth_fail"      → 401 (stop, log out)
  //           "client_error"   → 4xx (except 401/429) — show error, don't retry
  //           "transient_error"→ 5xx / 429 / network / stream cut (retry-able)
  async function doStreamRequest(body, onSources, onDelta) {
    let resp;
    try {
      resp = await fetch(`${API}/legal/ask`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
        body: JSON.stringify(body),
      });
    } catch (err) {
      return { status: "transient_error", error: err.message || "Error de red" };
    }

    if (!resp.ok) {
      if (resp.status === 401) return { status: "auth_fail" };
      if (resp.status === 402) {
        // No quedan consultas
        let detail = "Te quedaste sin preguntas. Suscríbete a un plan para seguir usando Pre-Legal.cl";
        try {
          const j = await resp.json();
          if (j && j.detail) detail = j.detail;
        } catch {
          /* ignore */
        }
        return { status: "no_consultas", error: detail };
      }
      if (resp.status === 429 || resp.status >= 500) {
        return { status: "transient_error", error: `Error ${resp.status}` };
      }
      let detail = `Error ${resp.status}`;
      try {
        const j = await resp.json();
        if (j && j.detail) detail = j.detail;
      } catch {
        /* ignore */
      }
      return { status: "client_error", error: detail };
    }

    const reader = resp.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let buffer = "";
    let gotDoneEvent = false;
    let serverErrorMsg = null;

    try {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop() || "";
        for (const raw of events) {
          const line = raw.split("\n").find((l) => l.startsWith("data:"));
          if (!line) continue;
          const payload = line.slice(5).trim();
          if (!payload) continue;
          let evt;
          try {
            evt = JSON.parse(payload);
          } catch {
            continue; // ignore malformed
          }
          if (evt.type === "sources") {
            onSources(evt.data || []);
          } else if (evt.type === "delta") {
            onDelta(evt.data || "");
          } else if (evt.type === "done") {
            gotDoneEvent = true;
          } else if (evt.type === "error") {
            serverErrorMsg = evt.data || "Error del servidor";
          }
        }
      }
    } catch (readErr) {
      // Network error while reading the stream → transient
      return {
        status: "transient_error",
        error: readErr.message || "Conexión interrumpida durante el stream",
      };
    }

    if (serverErrorMsg) {
      // Server-explicit error event: retry-able if it looks transient, otherwise fail
      // We treat them all as transient except explicit 401/400-ish strings.
      return { status: "transient_error", error: serverErrorMsg };
    }
    if (gotDoneEvent) {
      return { status: "done" };
    }
    // Stream ended without a "done" event → Cloudflare / proxy dropped the connection
    return { status: "transient_error", error: "Conexión interrumpida (sin evento 'done')" };
  }

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleNewChat = async () => {
    setError("");
    setInput("");
    setExpedienteJob(null);
    try {
      const conv = await createConversation([], [], mode);
      setConversations((prev) => [
        { conv_id: conv.conv_id, title: conv.title, mode: conv.mode, updated_at: conv.updated_at },
        ...prev,
      ]);
      skipNextSaveRef.current = true;
      setCurrentConvId(conv.conv_id);
      sessionIdRef.current = conv.session_id;
      setMessages([INITIAL_GREETING]);
      setSessionDocs([]);
    } catch {
      /* error already surfaced by createConversation */
    }
  };

  const handleLogout = async () => {
    // CRITICAL: flush any pending save BEFORE clearing local state. The user
    // may have just sent a message or uploaded a document and the debounce
    // hasn't fired yet — we must persist that to the backend before logout.
    try {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      // Also send a keepalive copy as belt-and-suspenders in case logout()
      // races faster than the awaited save.
      saveSnapshotKeepalive();
      if (saveRef.current) {
        await saveRef.current();
      }
    } catch {
      /* ignore — best effort */
    }
    clearPersistedState();
    setMessages([INITIAL_GREETING]);
    setSessionDocs([]);
    setExpedienteJob(null);
    setInput("");
    setError("");
    setConversations([]);
    setCurrentConvId("");
    sessionIdRef.current = _uuid();
    await logout();
    navigate("/", { replace: true });
  };

  // ----------------------------------------------------------------------
  // Templates: handler that converts a filled template into a natural prompt
  // and sends it through the normal chat flow (reusing handleSend's logic).
  // This way: SSE, retries, crosscheck, auto-save and .docx generation all
  // keep working for templated requests. The chat experience is unchanged —
  // the template just saves the user from typing the boilerplate.
  // ----------------------------------------------------------------------
  const openTemplate = (tpl) => {
    setActiveTemplate(tpl);
    const blank = {};
    (tpl.fields || []).forEach((fld) => {
      blank[fld.key] = "";
    });
    setTemplateData(blank);
  };

  const submitTemplate = async () => {
    if (!activeTemplate || loading) return;
    // Validate required fields
    const missing = (activeTemplate.fields || [])
      .filter((f) => f.required && !((templateData[f.key] || "").trim()))
      .map((f) => f.label);
    if (missing.length > 0) {
      setError("Faltan campos obligatorios: " + missing.join(", "));
      return;
    }
    const prompt = activeTemplate.buildPrompt(templateData);
    // Close the modal first so the user sees the message sending
    setTemplatesOpen(false);
    const tpl = activeTemplate;
    setActiveTemplate(null);
    setTemplateData({});
    setError("");
    // Send via the normal chat flow with an explicit override text (bypasses React state lag)
    handleSend({ preventDefault: () => {} }, prompt);
    void tpl;
  };

  const handleFilePicked = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ""; // allow re-picking same file later
    if (files.length === 0) return;
    setUploading(true);
    setError("");
    try {
      for (const file of files) {
        if (sessionDocs.length >= 20) {
          setError("Máximo 20 documentos activos en la conversación.");
          break;
        }
        // Offer expediente processing if PDF is large
        const isPdf = /\.pdf$/i.test(file.name);
        const sizeMb = file.size / (1024 * 1024);
        if (isPdf && sizeMb > 25) {
          const ok = window.confirm(
            `El PDF "${file.name}" pesa ${sizeMb.toFixed(1)} MB. Los PDFs grandes (expedientes, más de 15 páginas escaneadas) es mejor procesarlos como expediente, lo que divide el análisis en lotes y genera un resumen consolidado.\n\n` +
              `¿Quieres procesarlo como EXPEDIENTE (OK) o subirlo normal (Cancelar, solo analiza las primeras páginas)?`
          );
          if (ok) {
            await startExpedienteJob(file);
            continue;
          }
        }

        // Decide strategy: single-request for small files, chunked for large
        const useChunked = file.size > CHUNK_UPLOAD_THRESHOLD;
        let data;
        if (useChunked) {
          setUploadProgress({
            name: file.name,
            sent: 0,
            total: file.size,
            chunkIndex: 0,
            totalChunks: Math.max(1, Math.ceil(file.size / CHUNK_UPLOAD_SIZE)),
          });
          try {
            data = await uploadFileChunked(file, {
              password,
              sessionId: sessionIdRef.current,
              kind: "normal",
              onProgress: (p) => setUploadProgress({ name: file.name, ...p }),
            });
          } finally {
            setUploadProgress(null);
          }
        } else {
          const fd = new FormData();
          fd.append("file", file);
          fd.append("password", password);
          fd.append("session_id", sessionIdRef.current);
          const resp = await fetch(`${API}/legal/upload`, { method: "POST", credentials: "include", body: fd });
          if (!resp.ok) {
            let detail = `Error ${resp.status}`;
            try {
              const ct = resp.headers.get("content-type") || "";
              if (ct.includes("application/json")) {
                const j = await resp.json();
                if (j && j.detail) detail = j.detail;
              } else {
                const txt = await resp.text();
                if (txt) detail = `Error ${resp.status}: ${txt.slice(0, 200)}`;
              }
            } catch {
              /* ignore */
            }
            throw new Error(detail);
          }
          data = await resp.json();
        }

        // New format: always returns a doc_id (text or image_pages)
        if (data.kind === "image_pages") {
          if (data.truncated_from) {
            setError(
              `El PDF tenía ${data.truncated_from} páginas. Solo se indexaron las primeras ${data.pages}. ` +
                `Para analizarlo completo, súbelo como Expediente (PDFs >25 MB activan esa opción).`
            );
          }
          setSessionDocs((prev) => [
            ...prev,
            { doc_id: data.doc_id, name: data.name, kind: "image_pages", pages: data.pages },
          ]);
        } else {
          // kind === "text" or "image"
          setSessionDocs((prev) => [
            ...prev,
            {
              doc_id: data.doc_id,
              name: data.name,
              kind: data.kind,
              chunks: data.chunks,
              chars: data.chars,
              preview: data.preview,
            },
          ]);
        }
      }
    } catch (err) {
      setError(err.message || "Error al subir archivo");
    } finally {
      setUploading(false);
      setUploadProgress(null);
      // Flush save immediately so the new docs are persisted even if the
      // user closes the window right after the upload finishes.
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      setTimeout(() => {
        if (saveRef.current) saveRef.current();
      }, 50);
    }
  };

  const removeSessionDoc = (idx) => {
    setSessionDocs((prev) => prev.filter((_, i) => i !== idx));
  };

  // --- Expediente (large PDF) async processing ---
  const startExpedienteJob = async (file) => {
    try {
      let data;
      if (file.size > CHUNK_UPLOAD_THRESHOLD) {
        // Chunked path (avoids Cloudflare 413 on 25+ MB PDFs)
        setUploadProgress({
          name: file.name,
          sent: 0,
          total: file.size,
          chunkIndex: 0,
          totalChunks: Math.max(1, Math.ceil(file.size / CHUNK_UPLOAD_SIZE)),
        });
        try {
          data = await uploadFileChunked(file, {
            password,
            sessionId: sessionIdRef.current,
            kind: "expediente",
            onProgress: (p) => setUploadProgress({ name: file.name, ...p }),
          });
        } finally {
          setUploadProgress(null);
        }
      } else {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("password", password);
        fd.append("session_id", sessionIdRef.current);
        const resp = await fetch(`${API}/legal/expediente/start`, { method: "POST", credentials: "include", body: fd });
        if (!resp.ok) {
          let detail = `Error ${resp.status}`;
          try {
            const j = await resp.json();
            if (j && j.detail) detail = j.detail;
          } catch {
            /* ignore */
          }
          throw new Error(detail);
        }
        data = await resp.json();
      }
      setExpedienteJob({
        job_id: data.job_id,
        status: "processing",
        progress: 0,
        total: 0,
        filename: data.filename,
      });
      pollExpedienteJob(data.job_id);
    } catch (err) {
      setError("No se pudo iniciar el procesamiento del expediente: " + (err.message || ""));
      setUploadProgress(null);
    }
  };

  const pollExpedienteJob = async (jobId) => {
    const poll = async () => {
      try {
        const resp = await fetch(
          `${API}/legal/expediente/status/${jobId}?password=${encodeURIComponent(password)}`,
          { credentials: "include" }
        );
        if (!resp.ok) return;
        const data = await resp.json();
        setExpedienteJob((prev) => (prev ? { ...prev, ...data } : prev));
        if (data.status === "done") {
          // Inject the expediente summary as a sessionDoc using the backend-provided doc_id
          // (the backend already indexed it chunked for RAG retrieval).
          if (data.doc_id) {
            setSessionDocs((prev) => [
              ...prev,
              {
                doc_id: data.doc_id,
                name: data.doc_name || `${data.filename} (resumen)`,
                kind: "text",
                chunks: data.chunks,
              },
            ]);
          }
          // Add an assistant message announcing completion
          setMessages((prev) => [
            ...prev,
            {
              role: "assistant",
              content:
                `Listo, terminé de procesar el expediente **${data.filename}** (${data.total} páginas). ` +
                `Ya tengo el resumen consolidado indexado y disponible para nuestra conversación.\n\n` +
                `¿Qué necesitas que analice en particular?`,
              sources: [],
            },
          ]);
          setTimeout(() => setExpedienteJob(null), 4000);
          return;
        }
        if (data.status === "error") {
          setError("El procesamiento del expediente falló: " + (data.error || ""));
          setExpedienteJob(null);
          return;
        }
        setTimeout(poll, 2500);
      } catch {
        setTimeout(poll, 4000);
      }
    };
    poll();
  };

  // --- Auth gate view (kept as dead-code for resiliency; auth enforced by ProtectedRoute) ---
  if (!authed) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background:
            "linear-gradient(135deg, var(--pl-bg-from) 0%, var(--pl-bg-via) 50%, var(--pl-bg-to) 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "2rem",
          fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
        }}
      >
        <form
          data-testid="legal-auth-form"
          onSubmit={handleAuth}
          style={{
            background: "var(--pl-card)",
            border: "1px solid var(--pl-border)",
            borderRadius: 16,
            padding: "2.5rem",
            maxWidth: 420,
            width: "100%",
            boxShadow: "var(--pl-shadow-lg)",
          }}
        >
          <h1 style={{ color: "var(--pl-text-strong)", margin: 0, marginBottom: 8, fontSize: 26, letterSpacing: "-0.01em" }}>
            Pre-Legal.cl
          </h1>
          <p style={{ color: "var(--pl-text-muted)", fontSize: 14, marginBottom: 24 }}>
            Introduce la contraseña para acceder a tu asistente legal.
          </p>
          <input
            data-testid="legal-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Contraseña"
            autoFocus
            style={{
              width: "100%",
              padding: "12px 14px",
              borderRadius: 8,
              border: "1px solid var(--pl-border-strong)",
              background: "#fff",
              color: "var(--pl-text-strong)",
              fontSize: 15,
              outline: "none",
              boxSizing: "border-box",
            }}
          />
          {authError && (
            <p
              data-testid="legal-auth-error"
              style={{ color: "var(--pl-error)", fontSize: 13, marginTop: 10, marginBottom: 0 }}
            >
              {authError}
            </p>
          )}
          <button
            data-testid="legal-auth-submit"
            type="submit"
            disabled={authLoading}
            style={{
              width: "100%",
              marginTop: 18,
              padding: "12px",
              borderRadius: 8,
              border: "none",
              background: authLoading ? "#94a3b8" : "var(--pl-primary)",
              color: "white",
              fontSize: 15,
              fontWeight: 600,
              cursor: authLoading ? "wait" : "pointer",
            }}
          >
            {authLoading ? "Verificando..." : "Entrar"}
          </button>
        </form>
      </div>
    );
  }

  // --- Chat view (ChatGPT-style, centered, light theme) ---
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background:
          "radial-gradient(1400px 900px at 65% -10%, rgba(0, 200, 255, 0.22), transparent 60%), " +
          "radial-gradient(900px 700px at 20% 40%, rgba(0, 150, 196, 0.12), transparent 70%), " +
          "linear-gradient(180deg, #0a2236 0%, #0d2d44 8%, #0a1f30 28%, #06121b 65%, #03080d 100%)",
        display: "flex",
        justifyContent: isMobile ? "flex-start" : "center",
        fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
      }}
    >
      {/* === Conversations list (V2) === */}
      {/* On desktop: always-visible left column (320px). */}
      {/* On mobile: fullscreen overlay when sidebarOpen=true. */}
      {(!isMobile || sidebarOpen) && (
        <div
          className={isMobile ? "conv-list-v2-mobile" : "conv-list-v2-desktop"}
          style={
            isMobile
              ? {
                  position: "fixed",
                  inset: 0,
                  zIndex: 30,
                  background: "var(--pl-bg-solid)",
                }
              : {
                  width: sidebarWidth,
                  flexShrink: 0,
                  borderRight: "1px solid rgba(0, 200, 255, 0.18)",
                  background: "transparent",
                  height: "100vh",
                  position: "relative",
                }
          }
        >
          <ConversationsListV2
            conversations={conversations}
            currentConvId={currentConvId}
            convsLoading={convsLoading}
            onSelect={async (convId) => {
              if (convId === currentConvId) {
                if (isMobile) setSidebarOpen(false);
                return;
              }
              if (saveTimerRef.current) {
                clearTimeout(saveTimerRef.current);
                saveTimerRef.current = null;
                await saveCurrentConversation();
              }
              await loadConversationById(convId);
              if (isMobile) setSidebarOpen(false);
            }}
            onCreate={() => {
              handleNewChat();
              if (isMobile) setSidebarOpen(false);
            }}
            onDelete={deleteConversationById}
            onRename={renameConversation}
            onPatch={patchConversation}
            onOpenContext={(conv) => setContextMenuConv(conv)}
            onOpenLabels={(conv) => setLabelsModalConv(conv || { conv_id: null, tag: null, title: "Gestionar etiquetas" })}
            onLogout={handleLogout}
            onOpenTemplates={() => setTemplatesOpen(true)}
            onEnterSelectMode={() => { setSelectMode(true); setSelectedIds(new Set()); }}
            selectMode={selectMode}
            selectedIds={selectedIds}
            onToggleSelected={(id) => setSelectedIds((prev) => {
              const next = new Set(prev);
              if (next.has(id)) next.delete(id); else next.add(id);
              return next;
            })}
            onExitSelectMode={() => { setSelectMode(false); setSelectedIds(new Set()); }}
            onBulkPatch={async (patch) => {
              const ids = Array.from(selectedIds);
              await Promise.all(ids.map((id) => patchConversation(id, patch)));
              setSelectMode(false);
              setSelectedIds(new Set());
            }}
            onBulkDelete={async () => {
              const ids = Array.from(selectedIds);
              if (!ids.length) return;
              if (!window.confirm(`¿Eliminar ${ids.length} consulta(s)?`)) return;
              await Promise.all(ids.map((id) => deleteConversationById(id)));
              setSelectMode(false);
              setSelectedIds(new Set());
            }}
            isDesktopSplit={!isMobile}
          />
          {/* Desktop column resizer */}
          {!isMobile && (
            <div
              data-testid="conv-list-resizer"
              onMouseDown={(e) => {
                e.preventDefault();
                resizerActiveRef.current = true;
                document.body.style.cursor = "col-resize";
                document.body.style.userSelect = "none";
              }}
              style={{
                position: "absolute",
                top: 0,
                right: -3,
                width: 6,
                bottom: 0,
                cursor: "col-resize",
                zIndex: 10,
                background: "transparent",
              }}
              title="Arrastra para ajustar el ancho"
            />
          )}
        </div>
      )}
      <div
        className="legal-chat-shell"
        style={{
          position: "relative",
          width: "100%",
          flex: isMobile ? "0 0 100%" : 1,
          maxWidth: isMobile ? "100%" : "none",
          height: isMobile ? "100dvh" : "100vh",
          margin: 0,
          display: "flex",
          flexDirection: "column",
          borderRadius: 0,
          overflow: "hidden",
          background: "transparent",
          border: "none",
          boxShadow: "none",
        }}
      >
        {/* Conversation-switch shine overlay */}
        {shineKey > 0 && (
          <div
            key={shineKey}
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              overflow: "hidden",
              zIndex: 3,
              borderRadius: 0,
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: 0,
                width: "45%",
                background:
                  "linear-gradient(100deg, transparent 0%, rgba(37, 99, 235, 0.10) 48%, rgba(37, 99, 235, 0.18) 50%, rgba(37, 99, 235, 0.10) 52%, transparent 100%)",
                filter: "blur(14px)",
                mixBlendMode: "multiply",
                animation: "legalRoomShine 900ms ease-out both",
              }}
            />
          </div>
        )}
        {/* Header */}
        <header
          className="legal-chat-header"
          style={{
            padding: "12px 14px",
            minHeight: 64,
            borderBottom: "1px solid rgba(0, 200, 255, 0.18)",
            background: "transparent",
            backdropFilter: "blur(8px) saturate(140%)",
            WebkitBackdropFilter: "blur(8px) saturate(140%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, flex: 1 }}>
            {isMobile && (
              <button
                type="button"
                data-testid="legal-sidebar-toggle"
                onClick={() => setSidebarOpen(true)}
                aria-label="Volver a consultas"
                title="Volver a consultas"
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 999,
                  border: "none",
                  background: "transparent",
                  color: "var(--pl-primary)",
                  cursor: "pointer",
                  fontSize: 22,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: 0,
                  flexShrink: 0,
                  fontWeight: 700,
                }}
              >
                ‹
              </button>
            )}
            {/* Avatar PL with online dot */}
            <div style={{ position: "relative", flexShrink: 0 }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 999,
                  border: "1.5px solid var(--pl-primary)",
                  background: "rgba(0, 200, 255, 0.10)",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "var(--pl-primary)",
                  fontWeight: 800,
                  fontSize: 13,
                  letterSpacing: "0.05em",
                }}
              >
                PL
              </div>
              <div
                aria-hidden
                style={{
                  position: "absolute",
                  right: -1,
                  bottom: -1,
                  width: 10,
                  height: 10,
                  borderRadius: 999,
                  background: "#10b981",
                  border: "2px solid var(--pl-card)",
                }}
              />
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <h1
                data-testid="legal-page-title"
                style={{
                  color: "var(--pl-text-strong)",
                  margin: 0,
                  fontSize: 16,
                  fontWeight: 700,
                  letterSpacing: "-0.01em",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {(() => {
                  const cur = conversations.find((c) => c.conv_id === currentConvId);
                  return cur?.title || "Nueva consulta";
                })()}
              </h1>
              <p
                className="legal-header-subtitle"
                style={{
                  color: "var(--pl-text-muted)",
                  fontSize: 12,
                  margin: "1px 0 0",
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <span style={{ color: "var(--pl-primary)" }}>en línea</span>
                <span>·</span>
                <span style={{ color: "var(--pl-primary)", fontWeight: 600 }}>PL</span>
                <span style={{ letterSpacing: "0.08em" }}>ASISTENTE VIRTUAL</span>
              </p>
            </div>
          </div>
          <div className="legal-header-right" style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {/* Saving status badge — only visible during saves so it doesn't add noise */}
            {savingStatus !== "idle" && (
              <div
                data-testid="legal-saving-status"
                title={
                  savingStatus === "saving"
                    ? "Guardando tu conversación…"
                    : savingStatus === "saved"
                    ? "Guardado en la nube"
                    : "Error al guardar — intentando de nuevo"
                }
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "5px 10px",
                  borderRadius: 999,
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: "0.02em",
                  background:
                    savingStatus === "error"
                      ? "rgba(220, 38, 38, 0.14)"
                      : "rgba(0, 200, 255, 0.10)",
                  border:
                    savingStatus === "error"
                      ? "1px solid rgba(220, 38, 38, 0.40)"
                      : "1px solid rgba(0, 200, 255, 0.32)",
                  color:
                    savingStatus === "error"
                      ? "#dc2626"
                      : savingStatus === "saved"
                      ? "#10b981"
                      : "var(--pl-primary)",
                  whiteSpace: "nowrap",
                }}
              >
                {savingStatus === "saving" && (
                  <>
                    <span
                      aria-hidden="true"
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: "var(--pl-primary)",
                        animation: "legalSavingPulse 1.1s ease-in-out infinite",
                      }}
                    />
                    Guardando…
                  </>
                )}
                {savingStatus === "saved" && <>✓ Guardado</>}
                {savingStatus === "error" && <>⚠ Error</>}
              </div>
            )}
            {/* Consultas remaining indicator — only show when user has 0 left (red alert) */}
            {user && user.plan !== "unlimited" && user.consultas_remaining === 0 && (
              <div
                data-testid="consultas-remaining"
                onClick={() => setShowNoConsultasModal(true)}
                title="Sin preguntas — haz click para ver planes"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "6px 12px",
                  borderRadius: 999,
                  border: "1px solid rgba(220, 38, 38, 0.35)",
                  background: "rgba(220, 38, 38, 0.10)",
                  color: "var(--pl-error)",
                  fontSize: 12,
                  fontWeight: 600,
                  letterSpacing: "0.02em",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                <span style={{ fontSize: 11 }}>⚖️</span>
                Sin preguntas
              </div>
            )}
            {/* Mensual / Plantillas / Nuevo chat / Cerrar sesión REMOVED — now accessible from ⋯ dropdown */}
          </div>
        </header>

        {/* Messages area */}
        <div
          ref={scrollRef}
          data-testid="legal-messages-area"
          className="legal-messages-area"
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "24px 20px",
            display: "flex",
            flexDirection: "column",
            gap: 18,
          }}
        >
          {messages.map((m, idx) => {
            // Date separator pill ("Hoy" / "Ayer" / date) when day changes between consecutive messages
            const prev = idx > 0 ? messages[idx - 1] : null;
            let separator = null;
            if (m.t) {
              const cur = new Date(m.t);
              const curDay = cur.toDateString();
              const prevDay = prev?.t ? new Date(prev.t).toDateString() : null;
              if (curDay !== prevDay) {
                const today = new Date().toDateString();
                const yesterday = new Date(Date.now() - 86400000).toDateString();
                let label = cur.toLocaleDateString("es-CL", { day: "2-digit", month: "long" });
                if (curDay === today) label = "Hoy";
                else if (curDay === yesterday) label = "Ayer";
                separator = (
                  <div
                    key={`sep-${idx}`}
                    data-testid={`legal-date-sep-${idx}`}
                    style={{
                      alignSelf: "center",
                      padding: "4px 12px",
                      borderRadius: 999,
                      background: "rgba(100, 116, 139, 0.20)",
                      color: "var(--pl-text-muted)",
                      fontSize: 11,
                      fontWeight: 600,
                      letterSpacing: "0.02em",
                      margin: "4px 0",
                    }}
                  >
                    {label}
                  </div>
                );
              }
            }
            return (
              <React.Fragment key={idx}>
                {separator}
                <MessageBubble
                  msg={m}
                  password={password}
                  onQuote={handleQuote}
                  hasAssistantReplyAfter={
                    m.role === "user" &&
                    messages.slice(idx + 1).some(
                      (mm) => mm.role === "assistant" && mm.content && !mm.streaming
                    )
                  }
                />
              </React.Fragment>
            );
          })}
          {(
            (loading &&
              messages[messages.length - 1]?.role === "assistant" &&
              !messages[messages.length - 1]?.content) ||
            uploading ||
            !!uploadProgress ||
            (expedienteJob && expedienteJob.status !== "done")
          ) && (
            <div
              data-testid="legal-typing"
              style={{
                alignSelf: "flex-start",
                background: "var(--pl-card-soft)",
                border: "1px solid var(--pl-border)",
                padding: "12px 16px",
                borderRadius: 20,
                borderBottomLeftRadius: 6,
                color: "var(--pl-text)",
                fontSize: 13,
                display: "flex",
                alignItems: "center",
                gap: 8,
                boxShadow: "var(--pl-shadow-sm)",
                animation: "legalBubbleIn 150ms ease-out both",
              }}
            >
              <div style={{ display: "flex", gap: 4 }}>
                <Dot />
                <Dot delay="0.2s" />
                <Dot delay="0.4s" />
              </div>
              {(uploading || uploadProgress) && (
                <span style={{ opacity: 0.8 }}>
                  Subiendo {uploadProgress?.name ? `“${uploadProgress.name}”` : "archivo"}…
                </span>
              )}
              {!uploading && !uploadProgress && expedienteJob && expedienteJob.status !== "done" && (
                <span style={{ opacity: 0.8 }}>
                  Procesando expediente{expedienteJob.filename ? ` “${expedienteJob.filename}”` : ""}…
                </span>
              )}
            </div>
          )}
          {reconnectAttempt > 0 && (
            <div
              data-testid="legal-reconnecting"
              style={{
                alignSelf: "flex-start",
                background: "rgba(245, 158, 11, 0.12)",
                border: "1px solid rgba(245, 158, 11, 0.35)",
                color: "#b45309",
                padding: "6px 12px",
                borderRadius: 999,
                fontSize: 12,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 999,
                  background: "#f59e0b",
                  display: "inline-block",
                  animation: "legalBlink 1.2s infinite both",
                }}
              />
              Reconectando… (intento {reconnectAttempt}/3)
            </div>
          )}
          {error && (
            <div
              data-testid="legal-error"
              style={{
                background: "#fef2f2",
                border: "1px solid #fecaca",
                color: "#991b1b",
                padding: 12,
                borderRadius: 10,
                fontSize: 13,
                alignSelf: "center",
              }}
            >
              {error}
            </div>
          )}
        </div>

        {/* Input area */}
        <form
          onSubmit={handleSend}
          data-testid="legal-ask-form"
          className="legal-input-form"
          style={{
            padding: "12px 20px 20px",
            borderTop: "1px solid rgba(0, 200, 255, 0.14)",
            background: "transparent",
            backdropFilter: "blur(8px) saturate(140%)",
            WebkitBackdropFilter: "blur(8px) saturate(140%)",
          }}
        >
          {/* Active session documents (persist across turns) */}
          {sessionDocs.length > 0 && (
            <div
              data-testid="legal-session-docs"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 4,
                marginBottom: 8,
                padding: "6px 8px",
                background: "rgba(0, 200, 255, 0.08)",
                border: "1px solid rgba(0, 200, 255, 0.28)",
                borderRadius: 8,
              }}
            >
              <span style={{ fontSize: 10, color: "var(--pl-primary-deep)", fontWeight: 700, letterSpacing: "0.04em" }}>
                📎 DOCUMENTOS ACTIVOS EN LA CONVERSACIÓN ({sessionDocs.length})
              </span>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {sessionDocs.map((a, i) => (
                  <div
                    key={i}
                    style={{
                      background: "#ffffff",
                      border: "1px solid rgba(0, 200, 255, 0.35)",
                      borderRadius: 8,
                      padding: "6px 10px",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      fontSize: 12,
                      color: "#0f172a",
                      fontWeight: 500,
                      maxWidth: 260,
                      boxShadow: "0 2px 8px rgba(0, 200, 255, 0.10)",
                    }}
                  >
                    <span style={{ fontSize: 14 }}>{a.kind === "image" || a.kind === "image_pages" ? "🖼️" : "📄"}</span>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {a.name}
                      {a.chunks ? (
                        <span style={{ color: "#64748b", marginLeft: 4, fontSize: 10 }}>· {a.chunks} frag</span>
                      ) : a.pages ? (
                        <span style={{ color: "#64748b", marginLeft: 4, fontSize: 10 }}>· {a.pages} pág</span>
                      ) : null}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeSessionDoc(i)}
                      aria-label="Quitar documento de la conversación"
                      title="Quitar de la conversación"
                      style={{
                        background: "transparent",
                        border: "none",
                        color: "#64748b",
                        cursor: "pointer",
                        fontSize: 14,
                        lineHeight: 1,
                        padding: 0,
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Chunked upload progress (runs before expediente job begins, or during normal large upload) */}
          {uploadProgress && (
            <div
              data-testid="legal-upload-progress"
              style={{
                marginBottom: 8,
                padding: "10px 14px",
                background: "#ffffff",
                border: "1px solid rgba(0, 200, 255, 0.35)",
                borderRadius: 14,
                fontSize: 12,
                color: "#1e293b",
                boxShadow: "0 2px 10px rgba(0, 200, 255, 0.10)",
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 4, color: "#0f172a" }}>
                📤 Subiendo por fragmentos: {uploadProgress.name}
              </div>
              <div style={{ color: "#475569" }}>
                Fragmento {uploadProgress.chunkIndex || 0} / {uploadProgress.totalChunks} ·{" "}
                {(uploadProgress.sent / (1024 * 1024)).toFixed(1)} MB de{" "}
                {(uploadProgress.total / (1024 * 1024)).toFixed(1)} MB
              </div>
              <div
                style={{
                  marginTop: 6,
                  height: 6,
                  background: "rgba(0, 200, 255, 0.15)",
                  borderRadius: 999,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    background: "var(--pl-primary)",
                    width: `${Math.min(
                      100,
                      Math.round((uploadProgress.sent / Math.max(1, uploadProgress.total)) * 100)
                    )}%`,
                    transition: "width 0.25s ease",
                    borderRadius: 999,
                  }}
                />
              </div>
              <div style={{ marginTop: 4, fontSize: 11, color: "#64748b" }}>
                Dividimos el archivo en partes de 4 MB para evitar los límites de Cloudflare.
              </div>
            </div>
          )}

          {/* Expediente (large PDF) progress */}
          {expedienteJob && expedienteJob.status !== "done" && (
            <div
              data-testid="legal-expediente-progress"
              style={{
                marginBottom: 8,
                padding: "10px 14px",
                background: "rgba(0, 200, 255, 0.06)",
                border: "1px solid rgba(0, 200, 255, 0.30)",
                borderRadius: 14,
                fontSize: 12,
                color: "#1e293b",
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: 4, color: "var(--pl-text-strong)" }}>
                📋 Procesando expediente: {expedienteJob.filename}
              </div>
              <div style={{ color: "var(--pl-text-muted)" }}>
                {expedienteJob.status === "consolidating"
                  ? "Consolidando el resumen final..."
                  : `Progreso: ${expedienteJob.progress || 0}${expedienteJob.total ? ` / ${expedienteJob.total}` : ""} páginas analizadas`}
              </div>
              {expedienteJob.total > 0 && (
                <div
                  style={{
                    marginTop: 6,
                    height: 6,
                    background: "rgba(37, 99, 235, 0.15)",
                    borderRadius: 999,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      background: "var(--pl-primary)",
                      width: `${Math.min(100, Math.round((expedienteJob.progress / expedienteJob.total) * 100))}%`,
                      transition: "width 0.4s ease",
                      borderRadius: 999,
                    }}
                  />
                </div>
              )}
              <div style={{ marginTop: 4, fontSize: 11, color: "var(--pl-text-subtle)" }}>
                Esto puede tomar varios minutos. Puedes seguir chateando mientras tanto.
              </div>
            </div>
          )}

          <div
            className="legal-input-row"
            style={{
              display: "flex",
              gap: 10,
              background: "rgba(20, 23, 28, 0.85)",
              border: "1px solid rgba(0, 200, 255, 0.22)",
              borderRadius: 999,
              padding: "6px 6px 6px 6px",
              alignItems: "center",
              boxShadow: "var(--pl-shadow-sm)",
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,.txt,.xlsx,image/png,image/jpeg,image/webp,image/gif"
              multiple
              onChange={handleFilePicked}
              style={{ display: "none" }}
              data-testid="legal-file-input"
            />
            <button
              type="button"
              data-testid="legal-attach-btn"
              onClick={() => setAttachSheetOpen(true)}
              disabled={uploading || loading}
              title="Adjuntar foto, documento o tomar foto"
              className="legal-attach-btn"
              style={{
                width: 38,
                height: 38,
                borderRadius: 999,
                border: "1px solid rgba(0, 200, 255, 0.30)",
                background: uploading ? "rgba(0, 200, 255, 0.10)" : "rgba(0, 200, 255, 0.08)",
                color: "var(--pl-primary)",
                cursor: uploading || loading ? "wait" : "pointer",
                fontSize: 20,
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 0,
                flexShrink: 0,
                lineHeight: 1,
              }}
            >
              {uploading ? "…" : "+"}
            </button>
            <textarea
              ref={textareaRef}
              data-testid="legal-question-input"
              className="legal-textarea"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              maxLength={1500}
              placeholder={
                isMobile
                  ? "Escribe tu consulta legal…"
                  : "Escribe tu consulta legal... (Enter para enviar, Shift+Enter para salto de línea)"
              }
              style={{
                flex: 1,
                padding: "8px 10px",
                border: "none",
                background: "transparent",
                color: "var(--pl-text)",
                fontSize: 15,
                outline: "none",
                resize: "none",
                minHeight: 24,
                maxHeight: 260,
                overflow: "auto",
                fontFamily: "inherit",
                lineHeight: 1.5,
              }}
            />
            <button
              data-testid="legal-ask-submit"
              type="submit"
              disabled={loading || uploading || (!input.trim() && sessionDocs.length === 0)}
              className="legal-send-btn"
              style={{
                width: 40,
                height: 40,
                padding: 0,
                borderRadius: 999,
                border: "none",
                background:
                  loading || uploading || (!input.trim() && sessionDocs.length === 0)
                    ? "rgba(100, 116, 139, 0.30)"
                    : "var(--pl-primary)",
                color:
                  loading || uploading || (!input.trim() && sessionDocs.length === 0)
                    ? "#64748b"
                    : "#001018",
                fontWeight: 700,
                fontSize: 14,
                cursor:
                  loading || uploading || (!input.trim() && sessionDocs.length === 0)
                    ? "not-allowed"
                    : "pointer",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow:
                  loading || uploading || (!input.trim() && sessionDocs.length === 0)
                    ? "none"
                    : "0 6px 18px rgba(0, 200, 255, 0.40)",
                transition: "background 0.15s, box-shadow 0.15s",
              }}
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden>
                <path d="M2 21l21-9L2 3v7l15 2-15 2z" />
              </svg>
            </button>
          </div>
          <p
            className="legal-input-footer"
            style={{
              color: "var(--pl-text-muted)",
              fontSize: 11,
              margin: "8px 0 0",
              textAlign: "center",
              opacity: 0.55,
            }}
          >
            {isMobile
              ? "Pre-Legal.cl puede cometer errores. Verifica info crítica."
              : "Pre-Legal.cl puede cometer errores. Verifica información crítica. · Formatos: PDF, Word, TXT, Excel, imágenes (máx 15 MB)"}
          </p>
          <div
            data-testid="legal-input-footer-links"
            className="legal-input-footer"
            style={{
              display: "flex",
              gap: 10,
              alignItems: "center",
              justifyContent: "center",
              marginTop: 4,
              flexWrap: "wrap",
              opacity: 0.45,
            }}
          >
            <Link
              data-testid="legal-footer-terms"
              to="/prelegal/terminos"
              style={{ color: "var(--pl-text-muted)", textDecoration: "none", fontSize: 10.5, letterSpacing: "0.02em" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "var(--pl-primary)")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "var(--pl-text-muted)")}
            >
              Términos y Condiciones
            </Link>
            <span style={{ color: "var(--pl-text-subtle)", fontSize: 10 }}>·</span>
            <Link
              data-testid="legal-footer-privacy"
              to="/prelegal/privacidad"
              style={{ color: "var(--pl-text-muted)", textDecoration: "none", fontSize: 10.5, letterSpacing: "0.02em" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "var(--pl-primary)")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "var(--pl-text-muted)")}
            >
              Política de Privacidad
            </Link>
            <span style={{ color: "var(--pl-text-subtle)", fontSize: 10 }}>·</span>
            <Link
              data-testid="legal-footer-notice"
              to="/prelegal/aviso-legal"
              style={{ color: "var(--pl-text-muted)", textDecoration: "none", fontSize: 10.5, letterSpacing: "0.02em" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "var(--pl-primary)")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "var(--pl-text-muted)")}
            >
              Aviso Legal
            </Link>
          </div>
        </form>
      </div>

      {/* Floating bottom-right pill REMOVED — links are now inline under the disclaimer */}

      {/* Templates modal */}
      {templatesOpen && (
        <TemplatesModal
          activeTemplate={activeTemplate}
          templateData={templateData}
          onChangeField={(key, val) => setTemplateData((prev) => ({ ...prev, [key]: val }))}
          onPickTemplate={openTemplate}
          onBack={() => setActiveTemplate(null)}
          onClose={() => {
            setTemplatesOpen(false);
            setActiveTemplate(null);
            setTemplateData({});
          }}
          onSubmit={submitTemplate}
          loading={loading}
        />
      )}

      {/* No-consultas modal */}
      {showNoConsultasModal && (
        <NoConsultasModal onClose={() => setShowNoConsultasModal(false)} />
      )}

      {/* V2 redesign: conversation long-press context menu */}
      {contextMenuConv && (
        <ConversationContextMenu
          conv={contextMenuConv}
          onClose={() => setContextMenuConv(null)}
          onPatch={patchConversation}
          onRename={renameConversation}
          onDelete={deleteConversationById}
          onOpenLabels={(c) => setLabelsModalConv(c)}
        />
      )}

      {/* V2 redesign: labels picker modal */}
      {labelsModalConv && (
        <LabelsModal
          conv={labelsModalConv}
          onClose={() => {
            const wasBulk = Array.isArray(labelsModalConv?.__bulkIds);
            setLabelsModalConv(null);
            if (wasBulk) {
              setSelectMode(false);
              setSelectedIds(new Set());
            }
          }}
          onPatch={patchConversation}
        />
      )}

      {/* V2 redesign: attach bottom sheet (Foto / Cámara / Documento) */}
      {attachSheetOpen && (
        <AttachSheet
          onClose={() => setAttachSheetOpen(false)}
          onPickPhoto={(files) => handleFilePicked({ target: { files, value: "" } })}
          onPickCamera={(files) => handleFilePicked({ target: { files, value: "" } })}
          onPickDocument={(files) => handleFilePicked({ target: { files, value: "" } })}
        />
      )}
    </div>
  );
}

// Floating footer with links to Terms / Privacy / Legal notice
function TemplatesModal({
  activeTemplate,
  templateData,
  onChangeField,
  onPickTemplate,
  onBack,
  onClose,
  onSubmit,
  loading,
}) {
  // Group templates by category for the picker
  const grouped = LEGAL_TEMPLATES.reduce((acc, t) => {
    const cat = t.category || "Otros";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(t);
    return acc;
  }, {});

  return (
    <div
      data-testid="legal-templates-modal"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.45)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        zIndex: 50,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--pl-card)",
          border: "1px solid var(--pl-border)",
          borderRadius: 18,
          width: "100%",
          maxWidth: 640,
          maxHeight: "90vh",
          overflowY: "auto",
          color: "var(--pl-text)",
          boxShadow: "var(--pl-shadow-lg)",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "18px 22px",
            borderBottom: "1px solid var(--pl-border)",
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          {activeTemplate && (
            <button
              onClick={onBack}
              title="Volver al catálogo"
              style={{
                width: 30,
                height: 30,
                borderRadius: 999,
                border: "1px solid var(--pl-border)",
                background: "var(--pl-card-soft)",
                color: "var(--pl-text)",
                cursor: "pointer",
                fontSize: 14,
                padding: 0,
              }}
            >
              ←
            </button>
          )}
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, letterSpacing: "0.2em", color: "var(--pl-primary)", fontWeight: 700 }}>
              {activeTemplate ? "COMPLETA LOS DATOS" : "PLANTILLAS DE ESCRITOS"}
            </div>
            <div style={{ fontSize: 17, fontWeight: 700, color: "var(--pl-text-strong)", marginTop: 2 }}>
              {activeTemplate ? `${activeTemplate.icon} ${activeTemplate.name}` : "Elige un tipo de escrito"}
            </div>
          </div>
          <button
            data-testid="legal-templates-close"
            onClick={onClose}
            title="Cerrar"
            style={{
              width: 30,
              height: 30,
              borderRadius: 999,
              border: "1px solid var(--pl-border)",
              background: "var(--pl-card-soft)",
              color: "var(--pl-text)",
              cursor: "pointer",
              fontSize: 16,
              padding: 0,
            }}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: "18px 22px" }}>
          {!activeTemplate ? (
            // ---- Catalog ----
            <>
              <p style={{ fontSize: 13, color: "var(--pl-text-muted)", marginTop: 0, marginBottom: 18 }}>
                Selecciona una plantilla para ahorrar tiempo. El borrador se generará en tu
                conversación actual y podrás seguir conversando con el asistente normalmente
                para ajustes.
              </p>
              {Object.entries(grouped).map(([cat, items]) => (
                <div key={cat} style={{ marginBottom: 22 }}>
                  <div
                    style={{
                      fontSize: 11,
                      letterSpacing: "0.14em",
                      color: "var(--pl-primary)",
                      fontWeight: 700,
                      marginBottom: 8,
                    }}
                  >
                    {cat.toUpperCase()}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {items.map((t) => (
                      <button
                        key={t.id}
                        data-testid={`legal-template-${t.id}`}
                        onClick={() => onPickTemplate(t)}
                        style={{
                          textAlign: "left",
                          padding: "12px 14px",
                          borderRadius: 12,
                          border: "1px solid var(--pl-border)",
                          background: "var(--pl-card-soft)",
                          color: "var(--pl-text)",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          transition: "background 0.15s, border-color 0.15s, box-shadow 0.15s",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#fff";
                          e.currentTarget.style.borderColor = "rgba(37, 99, 235, 0.35)";
                          e.currentTarget.style.boxShadow = "var(--pl-shadow-sm)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "var(--pl-card-soft)";
                          e.currentTarget.style.borderColor = "var(--pl-border)";
                          e.currentTarget.style.boxShadow = "none";
                        }}
                      >
                        <span style={{ fontSize: 22 }}>{t.icon}</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--pl-text-strong)" }}>{t.name}</div>
                          <div style={{ fontSize: 12, color: "var(--pl-text-muted)", marginTop: 2 }}>
                            {t.description}
                          </div>
                        </div>
                        <span style={{ color: "var(--pl-text-subtle)", fontSize: 16 }}>›</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </>
          ) : (
            // ---- Form ----
            <>
              <p style={{ fontSize: 13, color: "var(--pl-text-muted)", marginTop: 0, marginBottom: 16 }}>
                {activeTemplate.description} · Los campos con <span style={{ color: "var(--pl-primary)" }}>*</span> son obligatorios.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {activeTemplate.fields.map((fld) => (
                  <div key={fld.key}>
                    <label
                      style={{
                        display: "block",
                        fontSize: 12,
                        color: "var(--pl-text)",
                        marginBottom: 5,
                        fontWeight: 600,
                      }}
                    >
                      {fld.label}
                      {fld.required && <span style={{ color: "var(--pl-primary)", marginLeft: 4 }}>*</span>}
                    </label>
                    {fld.type === "textarea" ? (
                      <textarea
                        data-testid={`legal-template-field-${fld.key}`}
                        value={templateData[fld.key] || ""}
                        onChange={(e) => onChangeField(fld.key, e.target.value)}
                        placeholder={fld.placeholder || ""}
                        rows={4}
                        style={inputStyle}
                      />
                    ) : (
                      <input
                        data-testid={`legal-template-field-${fld.key}`}
                        type={fld.type === "date" ? "text" : "text"}
                        value={templateData[fld.key] || ""}
                        onChange={(e) => onChangeField(fld.key, e.target.value)}
                        placeholder={fld.placeholder || ""}
                        style={inputStyle}
                      />
                    )}
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 18, display: "flex", justifyContent: "flex-end", gap: 10 }}>
                <button
                  onClick={onBack}
                  style={{
                    padding: "10px 18px",
                    borderRadius: 10,
                    border: "1px solid var(--pl-border)",
                    background: "var(--pl-card-soft)",
                    color: "var(--pl-text)",
                    fontSize: 13,
                    cursor: "pointer",
                  }}
                >
                  Volver
                </button>
                <button
                  data-testid="legal-template-submit"
                  onClick={onSubmit}
                  disabled={loading}
                  style={{
                    padding: "10px 22px",
                    borderRadius: 10,
                    border: "none",
                    background: loading ? "#94a3b8" : "var(--pl-primary)",
                    color: "#fff",
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: loading ? "default" : "pointer",
                    boxShadow: loading ? "none" : "0 6px 16px rgba(37, 99, 235, 0.28)",
                  }}
                >
                  {loading ? "Generando…" : "Generar escrito"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const inputStyle = {
  width: "100%",
  padding: "9px 12px",
  borderRadius: 10,
  border: "1px solid var(--pl-border-strong)",
  background: "#fff",
  color: "var(--pl-text-strong)",
  fontSize: 13,
  fontFamily: "inherit",
  outline: "none",
  boxSizing: "border-box",
  resize: "vertical",
};

function LegalFooterLinks() {
  const linkStyle = {
    color: "var(--pl-text-muted)",
    textDecoration: "none",
    fontSize: 11,
    letterSpacing: "0.02em",
    transition: "color 0.15s",
  };
  return (
    <div
      data-testid="legal-footer-links"
      className="legal-footer-links"
      style={{
        position: "fixed",
        right: 16,
        bottom: 10,
        zIndex: 5,
        display: "flex",
        gap: 10,
        alignItems: "center",
        padding: "6px 12px",
        background: "rgba(255, 255, 255, 0.85)",
        backdropFilter: "blur(12px) saturate(140%)",
        WebkitBackdropFilter: "blur(12px) saturate(140%)",
        border: "1px solid var(--pl-border)",
        borderRadius: 999,
        boxShadow: "var(--pl-shadow-sm)",
      }}
    >
      <Link
        data-testid="legal-footer-terms"
        to="/prelegal/terminos"
        style={linkStyle}
        onMouseEnter={(e) => (e.currentTarget.style.color = "var(--pl-primary)")}
        onMouseLeave={(e) => (e.currentTarget.style.color = "var(--pl-text-muted)")}
      >
        Términos y Condiciones
      </Link>
      <span style={{ color: "var(--pl-text-subtle)", fontSize: 10 }}>·</span>
      <Link
        data-testid="legal-footer-privacy"
        to="/prelegal/privacidad"
        style={linkStyle}
        onMouseEnter={(e) => (e.currentTarget.style.color = "var(--pl-primary)")}
        onMouseLeave={(e) => (e.currentTarget.style.color = "var(--pl-text-muted)")}
      >
        Política de Privacidad
      </Link>
      <span style={{ color: "var(--pl-text-subtle)", fontSize: 10 }}>·</span>
      <Link
        data-testid="legal-footer-notice"
        to="/prelegal/aviso-legal"
        style={linkStyle}
        onMouseEnter={(e) => (e.currentTarget.style.color = "var(--pl-primary)")}
        onMouseLeave={(e) => (e.currentTarget.style.color = "var(--pl-text-muted)")}
      >
        Aviso Legal
      </Link>
    </div>
  );
}

function LoadingIndicator() {
  const phrases = [
    "Consultando el código civil",
    "Revisando jurisprudencia",
    "Buscando precedentes",
    "Analizando tu consulta",
    "Redactando respuesta",
  ];
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % phrases.length);
    }, 2000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <span
      data-testid="legal-buscando-jurisprudencia"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        fontStyle: "italic",
        fontWeight: 500,
        textShadow: "none",
      }}
    >
      <span style={{ color: "var(--pl-primary)", fontSize: 18 }}>⚖</span>
      <span
        key={idx}
        style={{
          background:
            "linear-gradient(90deg, #1e3a8a 0%, #2563eb 35%, #60a5fa 60%, #1e3a8a 100%)",
          backgroundSize: "200% 100%",
          WebkitBackgroundClip: "text",
          backgroundClip: "text",
          WebkitTextFillColor: "transparent",
          color: "transparent",
          animation:
            "legalShimmer 1.8s linear infinite, legalPhraseFade 380ms ease-out",
          display: "inline-block",
        }}
      >
        {phrases[idx]}
      </span>
      <span
        style={{
          color: "var(--pl-primary)",
          display: "inline-flex",
          gap: 2,
          marginLeft: 1,
        }}
      >
        <span style={{ animation: "legalBlink 1.2s infinite", animationDelay: "0s" }}>.</span>
        <span style={{ animation: "legalBlink 1.2s infinite", animationDelay: "0.2s" }}>.</span>
        <span style={{ animation: "legalBlink 1.2s infinite", animationDelay: "0.4s" }}>.</span>
      </span>
    </span>
  );
}

function MessageBubble({ msg, password, onQuote, hasAssistantReplyAfter = false }) {
  const isUser = msg.role === "user";
  const isMobileBubble =
    typeof window !== "undefined" && window.innerWidth <= 768;

  // ---- Native gestures (mobile only) ----
  // Swipe-to-reply: swipe horizontally toward the message origin reveals a
  // reply hint and, on release past threshold, quotes the message.
  // Long-press: shows a small action menu (Copiar / Compartir / Citar).
  const [dragX, setDragX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [menu, setMenu] = useState(null); // null | { x, y }
  const longPressTimer = useRef(null);
  const startX = useRef(0);
  const startY = useRef(0);
  const movedRef = useRef(false);
  const intentRef = useRef(null); // 'horizontal' | 'vertical' | null

  // Direction of the swipe to "reply": user msg sits on the right → swipe LEFT
  // reveals reply icon on the right. Assistant on the left → swipe RIGHT.
  const swipeSign = isUser ? -1 : 1;
  const SWIPE_THRESHOLD = 56;
  const SWIPE_MAX = 90;

  const closeMenu = () => setMenu(null);

  // Auto-close menu when clicking/scrolling elsewhere
  useEffect(() => {
    if (!menu) return;
    const onAnywhere = () => setMenu(null);
    // Use timeout so the originating tap doesn't immediately close it
    const t = setTimeout(() => {
      window.addEventListener("scroll", onAnywhere, true);
      window.addEventListener("touchstart", onAnywhere, { passive: true });
      window.addEventListener("mousedown", onAnywhere);
    }, 50);
    return () => {
      clearTimeout(t);
      window.removeEventListener("scroll", onAnywhere, true);
      window.removeEventListener("touchstart", onAnywhere);
      window.removeEventListener("mousedown", onAnywhere);
    };
  }, [menu]);

  const plainText = typeof msg.content === "string" ? msg.content : "";

  const onTouchStart = (e) => {
    if (!isMobileBubble) return;
    if (e.touches.length !== 1) return;
    const t = e.touches[0];
    startX.current = t.clientX;
    startY.current = t.clientY;
    movedRef.current = false;
    intentRef.current = null;
    setDragging(true);
    // Long-press detection (cancelled on movement)
    clearTimeout(longPressTimer.current);
    longPressTimer.current = setTimeout(() => {
      if (!movedRef.current) {
        if (navigator.vibrate) try { navigator.vibrate(18); } catch { /* noop */ }
        setMenu({ x: t.clientX, y: t.clientY });
      }
    }, 480);
  };

  const onTouchMove = (e) => {
    if (!isMobileBubble || !dragging) return;
    const t = e.touches[0];
    const dx = t.clientX - startX.current;
    const dy = t.clientY - startY.current;
    if (!movedRef.current && (Math.abs(dx) > 6 || Math.abs(dy) > 6)) {
      movedRef.current = true;
      clearTimeout(longPressTimer.current);
      // Lock direction: if vertical wins, treat as scroll and stop tracking
      intentRef.current =
        Math.abs(dx) > Math.abs(dy) ? "horizontal" : "vertical";
    }
    if (intentRef.current === "horizontal") {
      // Only allow swipe in the bubble's own direction
      const allowed = swipeSign === 1 ? Math.max(0, dx) : Math.min(0, dx);
      const clamped = Math.max(-SWIPE_MAX, Math.min(SWIPE_MAX, allowed));
      setDragX(clamped);
    }
  };

  const onTouchEnd = () => {
    if (!isMobileBubble) return;
    clearTimeout(longPressTimer.current);
    setDragging(false);
    if (Math.abs(dragX) >= SWIPE_THRESHOLD && onQuote) {
      if (navigator.vibrate) try { navigator.vibrate(12); } catch { /* noop */ }
      onQuote(plainText);
    }
    setDragX(0);
  };

  const onTouchCancel = () => {
    clearTimeout(longPressTimer.current);
    setDragging(false);
    setDragX(0);
  };

  // ---- Action handlers (long-press menu) ----
  const doCopy = async (e) => {
    e.stopPropagation();
    setMenu(null);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(plainText);
      } else {
        const ta = document.createElement("textarea");
        ta.value = plainText;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand("copy"); } catch { /* noop */ }
        document.body.removeChild(ta);
      }
    } catch { /* noop */ }
  };

  const doShare = async (e) => {
    e.stopPropagation();
    setMenu(null);
    const shareData = { text: plainText, title: "Pre-Legal.cl" };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
        return;
      }
    } catch { /* user cancelled or unsupported */ }
    // Fallback: copy
    doCopy(e);
  };

  const doQuote = (e) => {
    e.stopPropagation();
    setMenu(null);
    if (onQuote) onQuote(plainText);
  };

  // Detect a legal document wrapped in markers to offer a Word download
  const DOC_START = "--- INICIO DOCUMENTO ---";
  const DOC_END = "--- FIN DOCUMENTO ---";
  const docMatch = !isUser && typeof msg.content === "string" && msg.content.includes(DOC_START) && msg.content.includes(DOC_END);
  let docContent = "";
  if (docMatch) {
    const s = msg.content.indexOf(DOC_START) + DOC_START.length;
    const e = msg.content.indexOf(DOC_END);
    docContent = msg.content.substring(s, e).trim();
  }

  const downloadDocx = async () => {
    if (!docContent) return;
    try {
      // Guess a filename from the first EN LO PRINCIPAL line
      let name = "documento-legal";
      const m = docContent.match(/EN LO PRINCIPAL[:\s]+([^\n.]+)/i);
      if (m) {
        name = m[1]
          .trim()
          .toLowerCase()
          .replace(/\*+/g, "")
          .replace(/[^a-z0-9áéíóúñ\s-]/gi, "")
          .trim()
          .replace(/\s+/g, "-")
          .slice(0, 60) || "documento-legal";
      }
      const resp = await fetch(`${API}/legal/generate-docx`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content: docContent, filename: name, password }),
      });
      if (!resp.ok) {
        let detail = `Error ${resp.status}`;
        try {
          const ct = resp.headers.get("content-type") || "";
          if (ct.includes("application/json")) {
            const j = await resp.json();
            if (j && j.detail) detail = j.detail;
          }
        } catch {
          /* ignore */
        }
        alert(detail);
        return;
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${name}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      alert("No se pudo descargar: " + (err.message || "error"));
    }
  };

  return (
    <div
      data-testid={`legal-msg-${msg.role}`}
      style={{
        alignSelf: isUser ? "flex-end" : "flex-start",
        maxWidth: "85%",
        display: "flex",
        flexDirection: isUser ? "column" : "row",
        alignItems: isUser ? "stretch" : "flex-end",
        gap: 8,
      }}
    >
      {/* Bot avatar PL next to assistant bubble (WhatsApp/iMessage style) */}
      {!isUser && (
        <div
          aria-hidden
          style={{
            width: 32,
            height: 32,
            borderRadius: 999,
            border: "1.5px solid var(--pl-primary)",
            background: "rgba(0, 200, 255, 0.08)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--pl-primary)",
            fontWeight: 800,
            fontSize: 11,
            letterSpacing: "0.05em",
            flexShrink: 0,
            marginBottom: 2,
          }}
        >
          PL
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0, flex: 1 }}>
      {/* Wrapper for the bubble: holds the swipe-to-reply hint behind the bubble */}
      <div
        style={{
          position: "relative",
          alignSelf: isUser ? "flex-end" : "flex-start",
          width: "fit-content",
          maxWidth: "100%",
        }}
      >
        {/* Swipe-to-reply hint (shown behind the bubble while dragging) */}
        {dragging && Math.abs(dragX) > 4 && (
          <div
            aria-hidden
            data-testid="legal-swipe-reply-hint"
            style={{
              position: "absolute",
              top: 0,
              bottom: 0,
              [isUser ? "right" : "left"]: -4,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 44,
              opacity: Math.min(1, Math.abs(dragX) / SWIPE_THRESHOLD),
              transform: `scale(${0.7 + Math.min(1, Math.abs(dragX) / SWIPE_THRESHOLD) * 0.3})`,
              color:
                Math.abs(dragX) >= SWIPE_THRESHOLD
                  ? "var(--pl-primary)"
                  : "#94a3b8",
              transition: "color 120ms ease",
              pointerEvents: "none",
              fontSize: 22,
              fontWeight: 700,
            }}
          >
            ↩
          </div>
        )}
        <div
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onTouchCancel={onTouchCancel}
          style={{
            // Light paper theme for assistant/user bubbles (chat readability)
            // Bubbles: cyan for user, navy-blue for assistant
            background: isUser
              ? "var(--pl-primary)"
              : "rgba(20, 45, 75, 0.72)",
            border: isUser
              ? "1px solid rgba(0, 200, 255, 0.45)"
              : "1px solid rgba(0, 200, 255, 0.28)",
            color: isUser ? "#ffffff" : "#e2eaf3",
            padding: "12px 16px",
            borderRadius: 20,
            borderBottomRightRadius: isUser ? 6 : 20,
            borderBottomLeftRadius: isUser ? 20 : 6,
            fontSize: 15,
            lineHeight: 1.6,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            boxShadow: isUser
              ? "0 6px 18px rgba(0, 200, 255, 0.25)"
              : "0 2px 12px rgba(0, 0, 0, 0.25)",
            animation: "legalBubbleIn 150ms ease-out both",
            transform: `translateX(${dragX}px)`,
            transition: dragging ? "none" : "transform 200ms ease",
            touchAction: "pan-y",
            userSelect: menu ? "none" : "text",
            WebkitUserSelect: menu ? "none" : "text",
            position: "relative",
          }}
        >
          {!isUser && msg.streaming && (!msg.content || !String(msg.content).trim()) ? (
            <LoadingIndicator />
          ) : (
            msg.content
        )}
          {/* Timestamp + checkmark — WhatsApp/iMessage style */}
          {!msg.streaming && msg.t && (
            <div
              data-testid={`legal-msg-meta-${msg.role}`}
              style={{
                marginTop: 4,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 4,
                fontSize: 10,
                opacity: 0.7,
                color: isUser ? "rgba(255,255,255,0.85)" : "var(--pl-text-muted)",
                fontWeight: 500,
              }}
            >
              <span>
                {(() => {
                  try {
                    return new Date(msg.t).toLocaleTimeString("es-CL", {
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: false,
                    });
                  } catch {
                    return "";
                  }
                })()}
              </span>
              {isUser && (
                <span style={{ fontSize: 11, lineHeight: 1, letterSpacing: "-0.06em" }}>
                  {hasAssistantReplyAfter ? "✓✓" : "✓"}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
      {docMatch && !msg.streaming && (
        <button
          type="button"
          data-testid="legal-docx-download"
          onClick={downloadDocx}
          style={{
            alignSelf: "flex-start",
            background: "var(--pl-primary)",
            border: "none",
            color: "#001018",
            padding: "8px 14px",
            borderRadius: 10,
            fontSize: 13,
            fontWeight: 700,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 6,
            boxShadow: "0 6px 18px rgba(0, 200, 255, 0.30)",
          }}
        >
          ⬇️ Descargar como Word (.docx)
        </button>
      )}
      {isUser && msg.attachments && msg.attachments.length > 0 && (
        <div
          data-testid="legal-msg-attachments"
          style={{ display: "flex", flexWrap: "wrap", gap: 6, alignSelf: "flex-end" }}
        >
          {msg.attachments.map((a, i) => (
            <div
              key={i}
              style={{
                background: "rgba(0, 200, 255, 0.12)",
                border: "1px solid rgba(0, 200, 255, 0.35)",
                borderRadius: 8,
                padding: "4px 10px",
                fontSize: 11,
                color: "#0f172a",
                display: "flex",
                alignItems: "center",
                gap: 6,
                maxWidth: 220,
              }}
            >
              <span>{a.kind === "image" ? "🖼️" : "📄"}</span>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {a.name}
              </span>
            </div>
          ))}
        </div>
      )}
      {!isUser && msg.sources && msg.sources.length > 0 && (
        <details
          data-testid="legal-sources"
          style={{
            background: "var(--pl-primary)",
            border: "1px solid rgba(0, 200, 255, 0.45)",
            borderRadius: 14,
            padding: "8px 14px",
            boxShadow: "0 4px 14px rgba(0, 200, 255, 0.20)",
          }}
        >
          <summary
            style={{
              cursor: "pointer",
              color: "#ffffff",
              fontSize: 13,
              fontWeight: 700,
              listStyle: "none",
            }}
          >
            📖 Fuentes consultadas ({msg.sources.length})
          </summary>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
            {msg.sources.map((s) => (
              <div
                key={s.n}
                data-testid={`legal-source-${s.n}`}
                style={{
                  background: "#ffffff",
                  border: "1px solid #e2e8f0",
                  borderRadius: 8,
                  padding: "8px 10px",
                }}
              >
                <div style={{ color: "#0f172a", fontSize: 12, fontWeight: 600 }}>
                  [Fuente {s.n}] {s.codigo} — {s.source}{" "}
                  <span style={{ color: "#64748b", fontWeight: 400 }}>
                    ({(s.score * 100).toFixed(1)}%)
                  </span>
                </div>
                <p
                  style={{
                    color: "#475569",
                    fontSize: 12,
                    lineHeight: 1.5,
                    margin: "6px 0 0",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {s.excerpt}
                </p>
              </div>
            ))}
          </div>
        </details>
      )}
      {/* Floating action menu (long-press) */}
      {menu && (
        <div
          data-testid="legal-msg-action-menu"
          onClick={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          style={{
            position: "fixed",
            left: Math.min(Math.max(menu.x - 90, 8), (typeof window !== "undefined" ? window.innerWidth - 200 : 200)),
            top: Math.max(menu.y - 64, 8),
            zIndex: 50,
            background: "#0f172a",
            color: "#fff",
            borderRadius: 12,
            padding: 4,
            display: "flex",
            gap: 2,
            boxShadow: "0 12px 30px rgba(0, 0, 0, 0.45)",
            animation: "legalBubbleIn 120ms ease-out both",
          }}
        >
          <button
            type="button"
            data-testid="legal-msg-copy"
            onClick={doCopy}
            style={menuBtnStyle}
          >
            Copiar
          </button>
          <button
            type="button"
            data-testid="legal-msg-share"
            onClick={doShare}
            style={menuBtnStyle}
          >
            Compartir
          </button>
          <button
            type="button"
            data-testid="legal-msg-quote"
            onClick={doQuote}
            style={{ ...menuBtnStyle, color: "var(--pl-primary)" }}
          >
            Citar
          </button>
        </div>
      )}
      </div>
    </div>
  );
}

const menuBtnStyle = {
  background: "transparent",
  border: "none",
  color: "#fff",
  padding: "8px 12px",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
  borderRadius: 8,
};

function Dot({ delay = "0s" }) {
  return (
    <span
      style={{
        width: 7,
        height: 7,
        borderRadius: 999,
        background: "var(--pl-primary)",
        display: "inline-block",
        animation: "legalBlink 1.4s infinite both",
        animationDelay: delay,
      }}
    />
  );
}

// Inject keyframes once
if (typeof document !== "undefined" && !document.getElementById("legal-chat-styles")) {
  const style = document.createElement("style");
  style.id = "legal-chat-styles";
  style.innerHTML = `
    @keyframes legalBlink{0%,80%,100%{opacity:0.2}40%{opacity:1}}
    @keyframes legalSavingPulse{
        0%, 100% { opacity: 1; transform: scale(1); }
        50% { opacity: 0.4; transform: scale(0.7); }
    }
    @keyframes legalShimmer{
      0%{background-position:200% 0;}
      100%{background-position:-200% 0;}
    }
    @keyframes legalPhraseFade{
      0%{opacity:0;transform:translateY(4px);}
      100%{opacity:1;transform:translateY(0);}
    }
    @keyframes legalBubbleIn{
      from{opacity:0;transform:translateY(8px) scale(0.985);}
      to{opacity:1;transform:translateY(0) scale(1);}
    }
    @keyframes legalRoomShine{
      0%{opacity:0;transform:translateX(-60%);}
      35%{opacity:1;}
      100%{opacity:0;transform:translateX(160%);}
    }

    /* === Mobile (≤768px) — WhatsApp/iMessage-style native chat UX === */
    @media (max-width: 768px) {
      /* Disable iOS rubber-band scroll bleeding into chrome */
      html, body {
          overscroll-behavior-y: none;
      }
      /* Full-bleed shell, hugs dynamic viewport so keyboard doesn't leave gaps */
      .legal-chat-shell {
        max-width: 100% !important;
        width: 100% !important;
        height: 100dvh !important;
        max-height: 100dvh !important;
        margin: 0 !important;
        border-radius: 0 !important;
        box-shadow: none !important;
        border: none !important;
      }

      /* Compact header (~48px) — WhatsApp-style */
      .legal-chat-header {
        padding: 8px 12px !important;
        gap: 6px !important;
        min-height: 48px !important;
        flex-wrap: nowrap !important;
      }
      .legal-chat-header h1 {
        font-size: 15px !important;
      }
      .legal-chat-header .legal-header-subtitle {
        display: none !important;
      }
      .legal-header-right {
        gap: 4px !important;
      }
      .legal-header-right > button {
        padding: 6px 9px !important;
        font-size: 12px !important;
        min-height: 34px !important;
      }
      /* Hide secondary header buttons on tiny screens to make room */
      .legal-btn-secondary-mobile-hide {
        display: none !important;
      }
      /* "Mensual" plan pill is non-essential in mobile chat — hide */
      .legal-consultas-pill {
        display: none !important;
      }
      /* Hide all chat header right-side buttons on mobile — focus on the chat */
      .legal-header-right > button {
        display: none !important;
      }
      /* But keep the saving status badge visible (it's a div, not a button) */
      .legal-header-right > div[data-testid="legal-saving-status"] {
        display: inline-flex !important;
      }

      /* Messages area: dense WhatsApp-style spacing */
      .legal-messages-area {
        padding: 10px 10px !important;
        gap: 6px !important;
        scroll-behavior: smooth;
      }
      /* Outer message wrappers — narrower so bubbles "breathe" like native apps */
      .legal-messages-area [data-testid^="legal-msg-"] {
        max-width: 80% !important;
      }
      /* Asymmetric tail margins (right for user, left for assistant) */
      .legal-messages-area [data-testid="legal-msg-user"] {
        margin-left: auto !important;
        margin-right: 0 !important;
      }
      .legal-messages-area [data-testid="legal-msg-assistant"] {
        margin-right: auto !important;
        margin-left: 0 !important;
      }
      /* Tighten the bubble itself (override inline styles via !important) */
      .legal-messages-area [data-testid^="legal-msg-"] > div:first-child {
        padding: 8px 12px !important;
        border-radius: 16px !important;
        font-size: 15px !important;
        line-height: 1.4 !important;
      }
      .legal-messages-area [data-testid="legal-msg-user"] > div:first-child {
        border-bottom-right-radius: 4px !important;
      }
      .legal-messages-area [data-testid="legal-msg-assistant"] > div:first-child {
        border-bottom-left-radius: 4px !important;
      }

      /* Input form: sticks to the bottom of the dvh shell, hugs keyboard */
      .legal-input-form {
        padding: 6px 8px env(safe-area-inset-bottom, 8px) !important;
        background: transparent !important;
      }
      .legal-input-row {
        gap: 6px !important;
        padding: 4px 4px 4px 6px !important;
        border-radius: 22px !important;
      }
      .legal-attach-btn {
        width: 38px !important;
        height: 38px !important;
        font-size: 18px !important;
      }
      .legal-textarea {
        font-size: 16px !important; /* prevents iOS zoom on focus */
        padding: 8px 6px !important;
        min-height: 24px !important;
        line-height: 1.35 !important;
      }
      .legal-textarea::placeholder {
        font-size: 14px !important;
      }
      .legal-send-btn {
        padding: 0 14px !important;
        height: 38px !important;
        font-size: 13px !important;
        min-width: 68px !important;
      }
      /* Disclaimer footer — visible when idle, fully collapses when typing */
      .legal-input-footer {
        font-size: 10px !important;
        line-height: 1.25 !important;
        margin-top: 4px !important;
        padding: 0 4px !important;
        max-height: 32px !important;
        opacity: 0.78 !important;
        overflow: hidden !important;
        transition: max-height 180ms ease, opacity 180ms ease, margin-top 180ms ease !important;
      }
      /* When user focuses the input (keyboard open), hide disclaimer entirely */
      .legal-input-form:focus-within .legal-input-footer {
        max-height: 0 !important;
        margin-top: 0 !important;
        opacity: 0 !important;
        pointer-events: none !important;
      }

      /* Hide floating legal links on mobile to free bottom space */
      .legal-footer-links {
        display: none !important;
      }
      /* Sidebar overlay backdrop on mobile */
      .legal-sidebar-backdrop {
        display: block !important;
      }
      .legal-sidebar-mobile-open {
        box-shadow: 0 0 40px rgba(15, 23, 42, 0.15) !important;
      }
      /* Session docs chips: wrap & shrink nicely */
      [data-testid="legal-session-docs"] {
        padding: 6px 8px !important;
      }
    }

    /* === Extra tight (≤420px, small phones) === */
    @media (max-width: 420px) {
      .legal-chat-header {
        min-height: 44px !important;
        padding: 6px 10px !important;
      }
      .legal-chat-header h1 {
        font-size: 14px !important;
      }
      .legal-header-right > button {
        padding: 6px 8px !important;
        font-size: 11px !important;
        min-height: 32px !important;
      }
      .legal-send-btn {
        min-width: 60px !important;
        padding: 0 12px !important;
      }
      .legal-messages-area {
        padding: 8px 8px !important;
      }
      .legal-messages-area [data-testid^="legal-msg-"] {
        max-width: 85% !important;
      }
    }
  `;
  document.head.appendChild(style);
}

// Sidebar listing saved cases (conversations)
function ConversationsSidebar({
  open,
  onClose,
  conversations,
  currentConvId,
  convsLoading,
  onSelect,
  onCreate,
  onDelete,
  onRename,
  onRegenerateTitle,
  onDownloadPdf,
}) {
  const SIDEBAR_WIDTH = 280;
  return (
    <>
      {/* Mobile backdrop: tap outside to close */}
      <div
        data-testid="legal-sidebar-backdrop"
        onClick={onClose}
        aria-hidden
        className="legal-sidebar-backdrop"
        style={{
          display: "none",
          position: "fixed",
          inset: 0,
          background: "rgba(15, 23, 42, 0.35)",
          zIndex: 19,
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
          transition: "opacity 0.2s ease",
        }}
      />
      <aside
        data-testid="legal-sidebar"
        aria-hidden={!open}
        className={open ? "legal-sidebar-mobile-open" : ""}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          bottom: 0,
          width: SIDEBAR_WIDTH,
          maxWidth: "85vw",
          transform: open ? "translateX(0)" : `translateX(-${SIDEBAR_WIDTH + 20}px)`,
          transition: "transform 0.25s ease",
          background: "rgba(255, 255, 255, 0.96)",
          borderRight: "1px solid var(--pl-border)",
          backdropFilter: "blur(12px) saturate(140%)",
          WebkitBackdropFilter: "blur(12px) saturate(140%)",
          zIndex: 20,
          display: "flex",
          flexDirection: "column",
          color: "var(--pl-text)",
          boxShadow: "var(--pl-shadow-md)",
        }}
      >
      <div
        style={{
          padding: "16px 14px 12px",
          borderBottom: "1px solid #e2e8f0",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <strong style={{ fontSize: 13, letterSpacing: "0.08em", color: "#475569" }}>
          MIS CASOS
        </strong>
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar sidebar"
          title="Ocultar"
          style={{
            marginLeft: "auto",
            width: 26,
            height: 26,
            borderRadius: 6,
            border: "1px solid #e2e8f0",
            background: "#f8f9fa",
            color: "#475569",
            fontSize: 14,
            cursor: "pointer",
            padding: 0,
          }}
        >
          ‹
        </button>
      </div>

      <button
        type="button"
        data-testid="legal-sidebar-new"
        onClick={onCreate}
        style={{
          margin: "10px 12px",
          padding: "10px 12px",
          borderRadius: 10,
          border: "none",
          background: "var(--pl-primary)",
          color: "#001018",
          fontSize: 13,
          fontWeight: 700,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          boxShadow: "0 6px 18px rgba(0, 200, 255, 0.30)",
        }}
      >
        ＋ Nueva conversación
      </button>

      <div
        data-testid="legal-sidebar-list"
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "4px 8px 16px",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {convsLoading && conversations.length === 0 && (
          <div style={{ padding: 14, fontSize: 12, color: "#64748b" }}>Cargando casos…</div>
        )}
        {!convsLoading && conversations.length === 0 && (
          <div style={{ padding: 14, fontSize: 12, color: "#64748b" }}>
            Aún no tienes casos guardados. Tu conversación actual aparecerá aquí apenas envíes un mensaje.
          </div>
        )}
        {conversations.map((c) => (
          <ConversationItem
            key={c.conv_id}
            conv={c}
            active={c.conv_id === currentConvId}
            onSelect={() => onSelect(c.conv_id)}
            onDelete={() => onDelete(c.conv_id)}
            onRename={(newTitle) => onRename(c.conv_id, newTitle)}
            onRegenerateTitle={() => onRegenerateTitle(c.conv_id)}
            onDownloadPdf={() => onDownloadPdf(c.conv_id, c.title)}
          />
        ))}
      </div>
    </aside>
    </>
  );
}

function ConversationItem({ conv, active, onSelect, onDelete, onRename, onRegenerateTitle, onDownloadPdf }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [tempTitle, setTempTitle] = useState(conv.title || "");

  const commitRename = () => {
    const t = tempTitle.trim();
    if (t && t !== conv.title) onRename(t);
    setRenaming(false);
  };

  return (
    <div
      data-testid={`legal-conv-item-${conv.conv_id}`}
      data-active={active ? "true" : "false"}
      style={{
        position: "relative",
        padding: "9px 10px",
        borderRadius: 8,
        background: active ? "rgba(0, 200, 255, 0.12)" : "transparent",
        border: active ? "1px solid rgba(0, 200, 255, 0.45)" : "1px solid transparent",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: 8,
      }}
      onMouseLeave={() => setMenuOpen(false)}
      onClick={renaming ? undefined : onSelect}
    >
      {renaming ? (
        <input
          autoFocus
          value={tempTitle}
          onChange={(e) => setTempTitle(e.target.value)}
          onBlur={commitRename}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commitRename();
            }
            if (e.key === "Escape") {
              setTempTitle(conv.title || "");
              setRenaming(false);
            }
          }}
          data-testid={`legal-conv-rename-${conv.conv_id}`}
          style={{
            flex: 1,
            padding: "4px 6px",
            background: "#fff",
            border: "1px solid #cbd5e1",
            borderRadius: 6,
            color: "#0f172a",
            fontSize: 13,
            outline: "none",
          }}
        />
      ) : (
        <span
          style={{
            flex: 1,
            fontSize: 13,
            color: active ? "var(--pl-primary-deep)" : "#1e293b",
            fontWeight: active ? 700 : 500,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={conv.title}
        >
          {conv.title || "Sin título"}
        </span>
      )}

      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setMenuOpen((v) => !v);
        }}
        aria-label="Opciones"
        data-testid={`legal-conv-menu-${conv.conv_id}`}
        style={{
          width: 22,
          height: 22,
          borderRadius: 6,
          border: "none",
          background: "transparent",
          color: "#64748b",
          cursor: "pointer",
          fontSize: 16,
          padding: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        ⋮
      </button>

      {menuOpen && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "absolute",
            top: "100%",
            right: 4,
            marginTop: 4,
            minWidth: 160,
            background: "#fff",
            border: "1px solid #e2e8f0",
            borderRadius: 8,
            boxShadow: "0 12px 32px rgba(0,0,0,0.15)",
            zIndex: 30,
            overflow: "hidden",
          }}
        >
          {[
            {
              label: "Renombrar",
              testid: `legal-conv-action-rename-${conv.conv_id}`,
              fn: () => {
                setRenaming(true);
                setMenuOpen(false);
              },
            },
            {
              label: "Título automático",
              testid: `legal-conv-action-autotitle-${conv.conv_id}`,
              fn: () => {
                onRegenerateTitle();
                setMenuOpen(false);
              },
            },
            {
              label: "📥 Descargar PDF",
              testid: `legal-conv-action-pdf-${conv.conv_id}`,
              fn: () => {
                onDownloadPdf();
                setMenuOpen(false);
              },
            },
            {
              label: "Eliminar",
              testid: `legal-conv-action-delete-${conv.conv_id}`,
              fn: () => {
                onDelete();
                setMenuOpen(false);
              },
              danger: true,
            },
          ].map((opt) => (
            <button
              key={opt.label}
              type="button"
              data-testid={opt.testid}
              onClick={opt.fn}
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "none",
                background: "transparent",
                color: opt.danger ? "#dc2626" : "#1e293b",
                fontSize: 12,
                textAlign: "left",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#f1f5f9")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// NoConsultasModal — shown when the user runs out of free queries
// -----------------------------------------------------------------------------
function NoConsultasModal({ onClose }) {
  const [loadingGateway, setLoadingGateway] = useState("");
  const [error, setError] = useState("");

  const subscribeWith = async (gateway) => {
    setError("");
    setLoadingGateway(gateway);
    const mod = await import("../lib/flowCheckout");
    const res = await mod.startCheckout("mensual", gateway);
    if (!res.ok) {
      setLoadingGateway("");
      setError(res.error || "No se pudo iniciar el pago.");
    }
  };

  const gatewayRows = [
    {
      code: "flow",
      logo: <FlowLogo />,
      name: "Flow.cl",
      sublabel: "Webpay, tarjetas chilenas, transferencia",
      badge: "Recomendado en Chile",
      color: "#0066ff",
    },
    {
      code: "mp",
      logo: <MercadoPagoLogo />,
      name: "Mercado Pago",
      sublabel: "Tarjeta de crédito o débito",
      color: "#00b1ea",
    },
  ];

  const showForeignHint = (() => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
      return !(tz === "America/Santiago" || tz === "America/Punta_Arenas" || tz === "Pacific/Easter");
    } catch {
      return false;
    }
  })();

  return (
    <div
      data-testid="no-consultas-modal"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.45)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 200,
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 520,
          background: "var(--pl-card)",
          border: "1px solid var(--pl-border)",
          borderRadius: 18,
          padding: "32px 28px",
          color: "var(--pl-text)",
          fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
          boxShadow: "var(--pl-shadow-lg)",
        }}
      >
        <div
          style={{
            width: 52,
            height: 52,
            borderRadius: 14,
            background: "var(--pl-primary-soft)",
            border: "1px solid rgba(37, 99, 235, 0.28)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 26,
            marginBottom: 18,
          }}
        >
          ⚖️
        </div>
        <h2
          style={{
            margin: 0,
            marginBottom: 10,
            fontSize: 22,
            fontWeight: 700,
            color: "var(--pl-text-strong)",
            letterSpacing: "-0.015em",
          }}
        >
          Te quedaste sin preguntas
        </h2>
        <p style={{ margin: 0, marginBottom: 22, fontSize: 14, color: "var(--pl-text-muted)", lineHeight: 1.55 }}>
          Has usado tus preguntas. Suscríbete al <strong>Plan Mensual</strong> ($9.990 CLP / mes — preguntas ilimitadas).
          Elige tu pasarela de pago:
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 22 }}>
          {gatewayRows.map((g) => (
            <button
              key={g.code}
              data-testid={`no-consultas-gateway-${g.code}`}
              onClick={() => subscribeWith(g.code)}
              disabled={!!loadingGateway}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 14,
                padding: "12px 14px",
                borderRadius: 12,
                background: "var(--pl-card-soft)",
                border: `1.5px solid ${g.color}33`,
                color: "var(--pl-text)",
                fontSize: 13,
                fontWeight: 500,
                cursor: loadingGateway ? "wait" : "pointer",
                textAlign: "left",
                opacity: loadingGateway && loadingGateway !== g.code ? 0.5 : 1,
                transition: "all 0.15s ease",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                <div aria-hidden="true" style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
                  {g.logo}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: "var(--pl-text-strong)", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    {g.name}
                    {g.badge && (
                      <span
                        style={{
                          fontSize: 10,
                          padding: "2px 8px",
                          borderRadius: 999,
                          background: g.color,
                          color: "#fff",
                          fontWeight: 700,
                          letterSpacing: "0.06em",
                        }}
                      >
                        {g.badge}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--pl-text-muted)", marginTop: 2 }}>{g.sublabel}</div>
                </div>
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: g.color, flexShrink: 0 }}>
                {loadingGateway === g.code ? "..." : "Continuar →"}
              </div>
            </button>
          ))}

          {showForeignHint ? (
            <div
              data-testid="no-consultas-foreign-hint"
              style={{
                display: "flex",
                gap: 8,
                alignItems: "flex-start",
                padding: "10px 12px",
                borderRadius: 10,
                background: "rgba(0, 177, 234, 0.08)",
                border: "1px solid rgba(0, 177, 234, 0.25)",
                marginTop: 4,
              }}
            >
              <Globe2 className="w-4 h-4" style={{ color: "#0084c1", flexShrink: 0, marginTop: 2 }} />
              <p style={{ margin: 0, fontSize: 12, lineHeight: 1.45, color: "var(--pl-text)" }}>
                <strong style={{ color: "#0084c1" }}>Mercado Pago</strong> acepta tarjetas
                extranjeras (Visa / Mastercard internacional). Si estás fuera de Chile, esta es
                tu mejor opción.
              </p>
            </div>
          ) : null}
        </div>

        {error && (
          <p
            data-testid="no-consultas-error"
            style={{
              margin: "0 0 14px 0",
              fontSize: 13,
              color: "var(--pl-error)",
              background: "#fef2f2",
              border: "1px solid #fecaca",
              padding: "8px 12px",
              borderRadius: 8,
            }}
          >
            {error}
          </p>
        )}

        <button
          data-testid="no-consultas-close"
          onClick={onClose}
          style={{
            width: "100%",
            padding: "10px 16px",
            borderRadius: 10,
            background: "var(--pl-card-soft)",
            color: "var(--pl-text-muted)",
            border: "1px solid var(--pl-border)",
            fontSize: 13,
            fontWeight: 500,
            cursor: "pointer",
          }}
        >
          Cerrar
        </button>
      </div>
    </div>
  );
}

